import { Controller } from "@hotwired/stimulus"

/**
 * Stimulus Controller: companies-v2-menu
 * 
 * Gerencia o cardápio digital de ultra-luxo com:
 * - Sistema de carrinho com persistência via localStorage
 * - Drawer lateral com transições suaves
 * - Detecção de status do estabelecimento (aberto/fechado)
 * - Integração com WhatsApp para envio de pedidos
 */
export default class extends Controller {
  static targets = [
    "header",
    "categoryNav",
    "categoryScroller",
    "cartDrawer",
    "cartOverlay",
    "cartPanel",
    "cartContainer",
    "itemCount",
    "floatingItemCount",
    "totalPrice",
    "closedBanner",
    "floatingCartBtn",

    "checkoutFooter",
    "addressForm",
    "deliveryRate",
    "presentialCartList",
    "presentialTotalPrice"
  ]

  static values = {
    companyOpen: Boolean,
    companySlug: String,
    whatsapp: String,
    clubCode: String,
    menuVersion: String
  }

  /**
   * Inicialização do controller
   */
  connect() {
    console.log('[VIPTable Menu] Controller connecting...', {
      targets: this.constructor.targets,
      hasTotalPriceTarget: this.hasTotalPriceTarget,

    })
    
    this.cart = this.loadCart()
    console.log('[VIPTable Menu] Cart loaded:', this.cart)
    const isCheckoutPage = !!document.querySelector('#checkout-phone')
    this.notifyDiscardedCart(isCheckoutPage)
    
    // Variável para armazenar a taxa de entrega
    this.deliveryRateValue = 0
    
    this.updateCartUI()
    this.setupScrollEffects()
    this.setupIntersectionObserver()
    this.setupCategorySyncOnScroll()
    
    // Aplicar estado de fechado se necessário
    if (!this.companyOpenValue) {
      this.applyClosedState()
    }
    
    // If the standalone checkout page is present, initialize its event listeners
    if (document.querySelector('#checkout-phone')) {
      try {
        this.setupCheckoutModalEventListeners()
        
        // Verificar se o carrinho está vazio na página de checkout
        if (this.cart.length === 0) {
          console.warn('[VIPTable Menu] Empty cart detected on checkout page, redirecting to menu')
          const slug = this.companySlugValue || window.VIPTableMenu?.companySlug
          if (slug) {
            window.location.href = `/companies/${slug}/menu`
          } else {
            window.history.back()
          }
          return
        }
      } catch (err) {
        console.error('[VIPTable Menu] failed to init checkout page listeners', err)
      }
    }

    console.log('[VIPTable Menu] Controller conectado', {
      isOpen: this.companyOpenValue,
      cartItems: this.cart.length
    })
  }

  /**
   * Cleanup ao desconectar
   */
  disconnect() {
    if (this.scrollObserver) {
      this.scrollObserver.disconnect()
    }
    if (this.categoryScrollHandler) {
      window.removeEventListener('scroll', this.categoryScrollHandler)
      window.removeEventListener('resize', this.categoryResizeHandler)
    }
  }

  // ==========================================
  // CART MANAGEMENT
  // ==========================================

  /**
   * Carrega o carrinho do localStorage
   */
  loadCart() {
    try {
      const cartData = localStorage.getItem(`viptable_cart_${this.companySlugValue}`)
      const cart = cartData ? JSON.parse(cartData) : []
      if (cart.length === 0) return cart

      const reason = this.cartDiscardReason()
      if (reason) {
        console.warn('[VIPTable Menu] Discarding stored cart:', { reason, cart })
        this.cartDiscardedReason = reason
        localStorage.removeItem(`viptable_cart_${this.companySlugValue}`)
        localStorage.removeItem(`viptable_cart_meta_${this.companySlugValue}`)
        return []
      }

      return cart
    } catch (e) {
      console.error('[VIPTable Menu] Erro ao carregar carrinho:', e)
      return []
    }
  }

  /**
   * Salva o carrinho no localStorage
   */
  saveCart() {
    try {
      localStorage.setItem(`viptable_cart_${this.companySlugValue}`, JSON.stringify(this.cart))
      // Prices in the cart come from the menu rendered on this page, so its version is the one stored
      localStorage.setItem(`viptable_cart_meta_${this.companySlugValue}`, JSON.stringify({
        savedOn: this.todayKey(),
        menuVersion: this.menuVersionValue
      }))
    } catch (e) {
      console.error('[VIPTable Menu] Erro ao salvar carrinho:', e)
    }
  }

  /**
   * Returns why the stored cart can no longer be trusted, or null if it is still valid:
   * - 'expired': saved on a previous day (or a legacy cart without metadata)
   * - 'menu_changed': the menu was edited after the cart was built (e.g. price change)
   */
  cartDiscardReason() {
    let meta = null
    try {
      meta = JSON.parse(localStorage.getItem(`viptable_cart_meta_${this.companySlugValue}`) || 'null')
    } catch (e) {
      meta = null
    }

    if (!meta || meta.savedOn !== this.todayKey()) return 'expired'
    if (this.menuVersionValue && meta.menuVersion !== this.menuVersionValue) return 'menu_changed'
    return null
  }

  /**
   * Local date as YYYY-MM-DD
   */
  todayKey() {
    const now = new Date()
    const pad = (n) => String(n).padStart(2, '0')
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
  }

  /**
   * Tells the customer their cart was emptied. On the checkout page the controller redirects
   * to the menu right away (empty cart), so the notice is deferred through sessionStorage.
   */
  notifyDiscardedCart(isCheckoutPage) {
    const noticeKey = `viptable_cart_discarded_${this.companySlugValue}`
    let reason = this.cartDiscardedReason

    try {
      if (reason && isCheckoutPage) {
        sessionStorage.setItem(noticeKey, reason)
        return
      }
      reason = reason || sessionStorage.getItem(noticeKey)
      sessionStorage.removeItem(noticeKey)
    } catch (e) {
      // sessionStorage unavailable (private mode); show the notice only when discarded on this page
    }

    if (!reason) return

    const message = reason === 'menu_changed'
      ? 'O cardápio foi atualizado e seu carrinho foi esvaziado para garantir os preços corretos.'
      : 'Seu carrinho anterior expirou. Monte seu pedido novamente para ver os preços atualizados.'
    this.showToast(message, 'warning')
  }

  /**
   * Applies the prices returned by the server when the checkout was sent with outdated values
   * (HTTP 409, code 'prices_changed'). Only the cart is touched: the checkout form keeps
   * everything the customer filled in, so they just review and send again.
   */
  applyServerPriceCorrections(data) {
    const corrections = Array.isArray(data?.items) ? data.items : []
    const updatedCart = []

    // Corrections are indexed by the position of the item in original_json_order, which mirrors this.cart
    this.cart.forEach((item, index) => {
      const fix = corrections.find(c => c.index === index)
      if (!fix) {
        updatedCart.push(item)
        return
      }
      if (fix.removed) return

      const basePrice = parseInt(fix.price, 10) || 0
      const additionals = fix.additionals || []
      const unitPrice = basePrice + additionals.reduce((s, a) => s + (parseInt(a.price, 10) || 0), 0)

      updatedCart.push({
        ...item,
        price: unitPrice,
        final_price: unitPrice,
        price_data: { price: basePrice, text: fix.price_text || '' },
        additionals,
        flavours: fix.flavours || []
      })
    })

    // The cart now reflects the current menu; store its version so a reload keeps the cart
    if (data?.menu_version) this.menuVersionValue = data.menu_version

    this.cart = updatedCart
    this.saveCart()
    this.updateCartUI()
    try {
      this.validateSendButton()
    } catch (e) {
      console.debug('[VIPTable Menu] validateSendButton unavailable after price correction', e)
    }

    this.showPriceUpdateNotice(data?.message, data?.changes || [])
  }

  /**
   * Modal listing what changed in the cart after the server price check
   */
  showPriceUpdateNotice(message, changes) {
    document.getElementById('price-update-modal')?.remove()

    const cartIsEmpty = this.cart.length === 0
    const subtotal = this.cart.reduce((s, it) => s + (parseInt(it.price, 10) * parseInt(it.quantity, 10)), 0)

    const modal = document.createElement('div')
    modal.id = 'price-update-modal'
    modal.className = 'fixed inset-0 z-[120] flex items-end sm:items-center justify-center'
    modal.innerHTML = `
      <div class="fixed inset-0 bg-black bg-opacity-50" data-price-update-close></div>
      <div class="relative rounded-t-3xl sm:rounded-2xl w-full sm:max-w-md max-h-[90vh] overflow-y-auto p-6 bg-white text-black shadow-2xl space-y-4">
        <h2 class="text-xl font-bold">Preços atualizados</h2>
        <p class="text-sm text-gray-600">${this.escapeHtml(message || 'Alguns itens do seu pedido tiveram os preços atualizados.')}</p>
        ${changes.length ? `<ul class="text-sm space-y-1 list-disc pl-5">${changes.map(c => `<li>${this.escapeHtml(c)}</li>`).join('')}</ul>` : ''}
        ${cartIsEmpty
          ? '<p class="text-sm font-medium">Seu carrinho ficou vazio. Escolha os itens novamente no cardápio.</p>'
          : `<p class="text-sm font-medium">Novo subtotal: ${this.formatCurrency(subtotal)}</p>`}
        <div class="flex justify-end">
          <button type="button" class="px-4 py-2 rounded-lg theme-btn-accent text-white" data-price-update-close>
            ${cartIsEmpty ? 'Voltar ao cardápio' : 'Revisar pedido'}
          </button>
        </div>
      </div>
    `

    modal.querySelectorAll('[data-price-update-close]').forEach(el => {
      el.addEventListener('click', () => {
        modal.remove()
        if (cartIsEmpty && this.companySlugValue) {
          window.location.href = `/companies/${this.companySlugValue}/menu`
        }
      })
    })

    document.body.appendChild(modal)
  }

  /**
   * Adiciona item ao carrinho
   */
  add(event) {
    event.preventDefault()
    event.stopPropagation()
    
    if (!this.companyOpenValue) {
      this.showToast('Estamos fechados no momento', 'warning')
      return
    }

    const button = event.currentTarget
    const card = button.closest('[data-food-id]')
    
    if (!card) return

    const foodId = card.dataset.foodId
    const foodName = card.dataset.foodName
    const foodPrice = parseInt(card.dataset.foodPrice, 10)
    const foodImage = (card.dataset.foodImage && card.dataset.foodImage.toString().trim()) ? card.dataset.foodImage : (card.querySelector('img') ? card.querySelector('img').src : '/fallbacks/food.jpg')
    const foodSlug = card.dataset.foodSlug

    console.log('[VIPTable Menu] Food data from DOM:', {
      foodId,
      foodName,
      foodPrice,
      foodPriceRaw: card.dataset.foodPrice,
      foodImage,
      foodSlug,
      cardDataset: card.dataset,
      cardImgSrc: card.querySelector('img') ? card.querySelector('img').src : null
    })

    // Verificar se os dados essenciais estão presentes
    if (!foodId || !foodName) {
      console.error('[VIPTable Menu] Missing essential food data:', { foodId, foodName })
      this.showToast('Erro ao adicionar item ao carrinho', 'error')
      return
    }

    // Verificar se o preço é válido
    if (isNaN(foodPrice) || foodPrice === 0) {
      console.error('[VIPTable Menu] Invalid price:', { foodPrice, raw: card.dataset.foodPrice })
      this.showToast('Erro: preço inválido', 'error')
      return
    }

    // Verificar se já existe no carrinho
    const existingIndex = this.cart.findIndex(item => item.id === foodId)
    
    if (existingIndex > -1) {
      this.cart[existingIndex].quantity += 1
      console.log('[VIPTable Menu] Item quantity increased:', this.cart[existingIndex])
    } else {
      const newItem = {
        id: foodId,
        name: foodName,
        price: foodPrice,
        image: foodImage,
        slug: foodSlug,
        quantity: 1
      }
      this.cart.push(newItem)
      console.log('[VIPTable Menu] New item added to cart:', newItem)
    }

    console.log('[VIPTable Menu] Current cart:', this.cart)

    this.saveCart()
    this.updateCartUI()
    this.animateAddToCart(button)
    this.showToast(`${foodName} adicionado ao carrinho`, 'success')
  }

  /**
   * Alias para add (compatibilidade com data-action)
   */
  addToCart(event) {
    this.add(event)
  }

  /**
   * Remove item do carrinho
   */
  remove(event) {
    event.preventDefault()
    event.stopPropagation()
    
    const foodId = String(event.currentTarget.dataset.foodId)
    console.log('[VIPTable Menu] Removing item:', { foodId, cartBefore: [...this.cart] })
    
    this.cart = this.cart.filter(item => String(item.id) !== foodId)
    
    console.log('[VIPTable Menu] Cart after removal:', this.cart)
    this.saveCart()
    this.updateCartUI()
    this.showToast('Item removido do carrinho', 'info')
  }

  /**
   * Atualiza quantidade do item
   */
  updateQuantity(event) {
    const foodId = String(event.currentTarget.dataset.foodId)
    const action = event.currentTarget.dataset.action?.includes('increment') ? 'increment' : 'decrement'
    
    const itemIndex = this.cart.findIndex(item => String(item.id) === foodId)
    
    if (itemIndex === -1) return

    if (action === 'increment') {
      this.cart[itemIndex].quantity += 1
    } else {
      if (this.cart[itemIndex].quantity > 1) {
        this.cart[itemIndex].quantity -= 1
      } else {
        this.cart.splice(itemIndex, 1)
      }
    }

    this.saveCart()
    this.updateCartUI()
  }

  /**
   * Incrementa quantidade
   */
  incrementQuantity(event) {
    event.preventDefault()
    event.stopPropagation()
    
    const btn = event.currentTarget
    const cartIndex = btn.dataset.cartIndex != null ? parseInt(btn.dataset.cartIndex, 10) : -1
    let itemIndex = cartIndex

    // Fallback para food-id (carrinho lateral usa food-id)
    if (itemIndex < 0 || itemIndex >= this.cart.length) {
      const foodId = String(btn.dataset.foodId)
      itemIndex = this.cart.findIndex(item => String(item.id) === foodId)
    }
    
    if (itemIndex < 0 || itemIndex >= this.cart.length) {
      console.error('[VIPTable Menu] Item not found in cart')
      return
    }

    this.cart[itemIndex].quantity += 1
    console.log('[VIPTable Menu] Item after increment:', this.cart[itemIndex])

    this.saveCart()
    this.updateCartUI()
  }

  /**
   * Decrementa quantidade
   */
  decrementQuantity(event) {
    event.preventDefault()
    event.stopPropagation()
    
    const btn = event.currentTarget
    const cartIndex = btn.dataset.cartIndex != null ? parseInt(btn.dataset.cartIndex, 10) : -1
    let itemIndex = cartIndex

    // Fallback para food-id (carrinho lateral usa food-id)
    if (itemIndex < 0 || itemIndex >= this.cart.length) {
      const foodId = String(btn.dataset.foodId)
      itemIndex = this.cart.findIndex(item => String(item.id) === foodId)
    }
    
    if (itemIndex < 0 || itemIndex >= this.cart.length) {
      console.error('[VIPTable Menu] Item not found in cart')
      return
    }

    if (this.cart[itemIndex].quantity > 1) {
      this.cart[itemIndex].quantity -= 1
      this.saveCart()
      this.updateCartUI()
    } else {
      // Quantity would reach 0 — confirm removal
      const itemName = this.cart[itemIndex].name || 'este item'
      this._confirmRemoval(itemName, () => {
        this.cart.splice(itemIndex, 1)
        this.saveCart()
        this.updateCartUI()
      })
    }
  }

  /**
   * Limpa o carrinho
   */
  clear() {
    this.cart = []
    this.saveCart()
    this.updateCartUI()
    this.showToast('Carrinho limpo', 'info')
  }

  /**
   * Alias para clear
   */
  clearCart() {
    this.clear()
  }

  /**
   * Atualiza toda a UI do carrinho
   */
  updateCartUI() {
    this.updateItemCount()
    this.updateTotalPrice()
    this.renderCartItems()

    // render presential inline cart when present
    if (this.hasPresentialCartListTarget) this.renderPresentialCartItems()

    // Ensure V2 footer send button reflects cart state (and keep legacy in sync)
    try {
      const footerBtn = document.getElementById('checkout-send-whatsapp')
      
      console.log('[VIPTable Menu] Footer button check:', {
        btn: footerBtn,
        cartLength: this.cart.length
      })

      if (this.cart.length === 0) {
        if (footerBtn) {
          footerBtn.classList.add('hidden')
          footerBtn.disabled = true
        }
      } else {
        // Cart has items: ensure button is visible and enabled
        // BUT respect the delayed-show timeout set by the checkout flow —
        // if the button is mid-delay, don't force-show it early.
        if (footerBtn && !footerBtn._delayedShowTimeout) {
          footerBtn.classList.remove('hidden')
          footerBtn.disabled = false
          console.log('[VIPTable Menu] Showing send button')
        }
      }

      // legacy button handled inside updateTotalPrice() for consistency
    } catch (err) {
      console.error('[VIPTable Menu] Error syncing footer send button', err)
    }

    // Sync the static "Finalizar pedido" button inside the cart drawer
    try {
      const drawerSendBtn = document.getElementById('cart-drawer-send-btn')

      if (drawerSendBtn) {
        const minimumValue = parseInt(drawerSendBtn.dataset.minimumValue || '0', 10)
                          || window.VIPTableMenu?.minimumValue || 0
        const subtotal = this.cart.reduce((s, it) => {
          const unitPrice = (it.hasOwnProperty('final_price') && it.final_price !== null) ? it.final_price : it.price
          return s + (unitPrice * it.quantity)
        }, 0)
        const belowMinimum = minimumValue > 0 && subtotal < minimumValue

        if (this.cart.length === 0 || belowMinimum) {
          drawerSendBtn.disabled = true
          drawerSendBtn.classList.add('opacity-50', 'cursor-not-allowed', 'pointer-events-none')
          if (belowMinimum && minimumValue > 0) {
            drawerSendBtn.textContent = `Pedido mínimo de ${this.formatCurrency(minimumValue)}`
          } else {
            drawerSendBtn.textContent = 'Finalizar pedido'
          }
        } else {
          drawerSendBtn.disabled = false
          drawerSendBtn.classList.remove('opacity-50', 'cursor-not-allowed', 'pointer-events-none')
          drawerSendBtn.textContent = 'Finalizar pedido'
        }
      }
    } catch (err) {
      console.error('[VIPTable Menu] Error syncing drawer send button', err)
    }

    console.log('[VIPTable Menu] Cart UI updated', {
      cart: this.cart,
      cartLength: this.cart.length,
      totalText: this.hasTotalPriceTarget ? this.totalPriceTarget?.textContent : null,
      totalTargetExists: this.hasTotalPriceTarget
    })
  }

  /**
   * Atualiza contador de itens
   */
  updateItemCount() {
    const totalItems = this.cart.reduce((sum, item) => sum + item.quantity, 0)
    
    if (this.hasItemCountTarget) {
      this.itemCountTarget.textContent = totalItems
      this.itemCountTarget.classList.toggle('hidden', totalItems === 0)
    }
    
    if (this.hasFloatingItemCountTarget) {
      this.floatingItemCountTarget.textContent = totalItems
      this.floatingItemCountTarget.classList.toggle('hidden', totalItems === 0)
    }
  }

  /**
   * Atualiza preço total
   */
  updateTotalPrice() {
    const total = this.cart.reduce((sum, item) => {
      // Treat `final_price` as a per-unit override (if present), otherwise use `price` per unit
      const unitPrice = (item.hasOwnProperty('final_price') && item.final_price !== null) ? item.final_price : item.price
      const itemTotal = unitPrice * item.quantity
      console.log('[VIPTable Menu] Item calculation:', {
        name: item.name,
        price: item.price,
        quantity: item.quantity,
        final_price: item.final_price,
        unitPrice,
        itemTotal,
        runningTotal: sum + itemTotal
      })
      return sum + itemTotal
    }, 0)
    
    // Adicionar taxa de entrega ao total
    const totalWithDelivery = total + (this.deliveryRateValue || 0)
    const formattedTotal = this.formatCurrency(totalWithDelivery)
    
    console.log('[VIPTable Menu] Total price:', { 
      cartTotal: total, 
      deliveryRate: this.deliveryRateValue,
      totalWithDelivery,
      formattedTotal, 
      hasTotalPriceTarget: this.hasTotalPriceTarget 
    })

    // Update Stimulus target if present
    if (this.hasTotalPriceTarget) {
      this.totalPriceTarget.textContent = formattedTotal
    }

    // Also update any DOM elements that use the data attribute (in case multiple instances exist)
    try {
      const selector = '[data-companies--v2--menu-target="totalPrice"]'
      document.querySelectorAll(selector).forEach(el => {
        el.textContent = formattedTotal
      })
    } catch (e) {
      console.debug('[VIPTable Menu] Error updating fallback total elements', e)
    }

    // --- Legacy compatibility: update legacy total and legacy send button if present ---
    try {
      const legacyTotal = document.getElementById('total_value')
      if (legacyTotal) legacyTotal.textContent = formattedTotal

      const legacySendBtn = document.getElementById('btn-send-simple-order')
      if (legacySendBtn) {
        if (this.cart.length === 0) {
          legacySendBtn.classList.add('disabled')
          legacySendBtn.setAttribute('aria-disabled', 'true')
        } else {
          legacySendBtn.classList.remove('disabled')
          legacySendBtn.removeAttribute('aria-disabled')
        }
      }
    } catch (err) {
      console.debug('[VIPTable Menu] Error updating legacy total/button', err)
    }
  }

  /**
   * Renderiza os itens do carrinho
   */
  /**
   * Renderiza o carrinho inline na página de experiência presencial
   * Suporta incrementar, decrementar e remover itens
   */
  renderPresentialCartItems() {
    if (!this.hasPresentialCartListTarget) return

    const targets = this.presentialCartListTargets

    // Atualiza total presencial
    if (this.hasPresentialTotalPriceTarget) {
      const total = this.cart.reduce((s, it) => s + (parseInt(it.price, 10) * parseInt(it.quantity, 10)), 0)
      this.presentialTotalPriceTarget.textContent = this.formatCurrency(total)
    }

    if (this.cart.length === 0) {
      const emptyHtml = `
        <div class="text-center py-10">
          <svg class="w-14 h-14 mx-auto mb-3" style="color:var(--theme-border);" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"/>
          </svg>
          <p style="color:var(--theme-text-muted);" class="text-sm">Nenhum item no carrinho.</p>
        </div>`
      targets.forEach(t => t.innerHTML = emptyHtml)
      return
    }

    const html = this.cart.map((item, idx) => {
      const itemTotal = parseInt(item.price, 10) * parseInt(item.quantity, 10)
      const img = (item.image && item.image.toString().trim()) ? item.image : '/fallbacks/food.jpg'

      // Detalhes extras do item
      const variationText = item.price_data?.text || ''
      const additionals   = item.additionals || []
      const optionals     = item.optionals   || []
      const flavours      = item.flavours    || []
      const observations  = (item.observations || '').trim()

      const tagStyle   = `display:inline-block;font-size:0.65rem;padding:0.1rem 0.45rem;border-radius:999px;margin:0.1rem 0.1rem 0.1rem 0;`
      const addTagStyle = tagStyle + `background:rgba(var(--theme-accent-rgb,212,175,55),0.12);color:var(--theme-accent);border:1px solid rgba(var(--theme-accent-rgb,212,175,55),0.25);`
      const optTagStyle = tagStyle + `background:rgba(239,68,68,0.08);color:#f87171;border:1px solid rgba(239,68,68,0.2);`
      const flvTagStyle = tagStyle + `background:rgba(99,179,237,0.1);color:#63b3ed;border:1px solid rgba(99,179,237,0.2);`

      const variationHtml = variationText
        ? `<span style="${addTagStyle}">📐 ${this.escapeHtml(variationText)}</span>`
        : ''

      const addHtml = additionals.map(a => {
        const label = a.name || a.text || ''
        return label ? `<span style="${addTagStyle}">+ ${this.escapeHtml(label)}</span>` : ''
      }).join('')

      const flvHtml = flavours.map(f => {
        const label = f.name || f.text || ''
        return label ? `<span style="${flvTagStyle}">🍕 ${this.escapeHtml(label)}</span>` : ''
      }).join('')

      const optHtml = optionals.map(o => {
        const label = o.name || o.text || ''
        return label ? `<span style="${optTagStyle}">✗ ${this.escapeHtml(label)}</span>` : ''
      }).join('')

      const tagsHtml = variationHtml + flvHtml + addHtml + optHtml
      const tagsBlock = tagsHtml
        ? `<div style="margin-top:0.35rem;line-height:1.6;">${tagsHtml}</div>`
        : ''

      const obsBlock = observations
        ? `<p style="margin-top:0.35rem;font-size:0.7rem;font-style:italic;color:var(--theme-text-muted);border-left:2px solid rgba(212,175,55,0.3);padding-left:0.4rem;">"${this.escapeHtml(observations)}"</p>`
        : ''

      return `
        <div class="flex gap-3 p-4 rounded-xl mb-2" style="background:var(--theme-bg-card);border:1px solid var(--theme-border);" data-food-id="${item.id}">
          <div class="w-14 h-14 rounded-lg overflow-hidden flex-shrink-0">
            <img src="${img}" alt="${this.escapeHtml(item.name)}" class="w-full h-full object-cover">
          </div>

          <div class="flex-1 min-w-0">
            <p class="font-medium text-sm" style="color:var(--theme-text-heading);line-height:1.3;">${this.escapeHtml(item.name)}</p>
            <p class="text-xs mt-0.5" style="color:var(--theme-text-muted);">${this.formatCurrency(parseInt(item.price, 10))} cada</p>
            ${tagsBlock}
            ${obsBlock}

            <div class="flex items-center justify-between mt-2">
              <div class="flex items-center gap-2">
                <button
                  class="w-7 h-7 rounded-full flex items-center justify-center transition-colors"
                  style="background:var(--theme-bg-card-hover); border:1px solid var(--theme-border);"
                  data-food-id="${item.id}"
                  data-cart-index="${idx}"
                  data-action="click->companies--v2--menu#decrementQuantity"
                  aria-label="Diminuir quantidade">
                  <svg class="w-3.5 h-3.5" style="color:var(--theme-text);" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 12H4"/>
                  </svg>
                </button>
                <span class="w-8 text-center font-medium text-sm" style="color:var(--theme-text-heading);">${item.quantity}</span>
                <button
                  class="w-7 h-7 rounded-full flex items-center justify-center transition-colors"
                  style="background:var(--theme-bg-card-hover); border:1px solid var(--theme-border-accent);"
                  data-food-id="${item.id}"
                  data-cart-index="${idx}"
                  data-action="click->companies--v2--menu#incrementQuantity"
                  aria-label="Aumentar quantidade">
                  <svg class="w-3.5 h-3.5" style="color:var(--theme-accent);" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
                  </svg>
                </button>
                <button
                  class="w-6 h-6 rounded-full flex items-center justify-center transition-colors"
                  style="background:rgba(239,68,68,0.12);"
                  data-food-id="${item.id}"
                  data-cart-index="${idx}"
                  data-action="click->companies--v2--menu#removeItemPresential"
                  aria-label="Remover item">
                  <svg class="w-3 h-3" style="color:#ef4444;" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                </button>
              </div>
              <span class="text-sm font-semibold" style="color:var(--theme-accent);">${this.formatCurrency(itemTotal)}</span>
            </div>
          </div>
        </div>`
    }).join('')

    targets.forEach(t => t.innerHTML = html)
  }

  /**
   * Remove item completamente do carrinho (usado na experiência presencial)
   */
  removeItemPresential(event) {
    event.preventDefault()
    event.stopPropagation()
    const btn = event.currentTarget
    const cartIndex = btn.dataset.cartIndex != null ? parseInt(btn.dataset.cartIndex, 10) : -1

    let itemIndex = cartIndex
    if (itemIndex < 0 || itemIndex >= this.cart.length) {
      const foodId = String(btn.dataset.foodId)
      itemIndex = this.cart.findIndex(item => String(item.id) === foodId)
    }
    if (itemIndex < 0 || itemIndex >= this.cart.length) return

    const itemName = this.cart[itemIndex].name || 'este item'
    this._confirmRemoval(itemName, () => {
      this.cart.splice(itemIndex, 1)
      this.saveCart()
      this.updateCartUI()
    })
  }

  onPresentialVipPhoneInput(event) {
    const input = event.target
    const stripDigits = (v) => (v || '').toString().replace(/\D/g, '')
    const formatPhone = (digits) => {
      const a = digits.slice(0, 2)
      const b = digits.slice(2, 7)
      const c = digits.slice(7, 11)
      let out = ''
      if (a.length) out += `(${a}` + (a.length === 2 ? ') ' : '')
      if (b.length) out += b
      if (c.length) out += `-${c}`
      return out
    }

    const cursorPos = input.selectionStart
    const oldValue = input.value
    const digits = stripDigits(oldValue).slice(0, 11)
    const newValue = formatPhone(digits)
    input.value = newValue
    // Restaura a posição do cursor com base no número de dígitos antes dele,
    // evitando que o cursor salte para o final ao pressionar Backspace no DDD.
    const digitsBeforeCursor = stripDigits(oldValue.slice(0, cursorPos)).length
    if (digitsBeforeCursor === 0) {
      input.setSelectionRange(0, 0)
    } else {
      let digitCount = 0
      let newCursorPos = newValue.length
      for (let i = 0; i < newValue.length; i++) {
        if (/\d/.test(newValue[i])) {
          digitCount++
          if (digitCount === digitsBeforeCursor) { newCursorPos = i + 1; break }
        }
      }
      input.setSelectionRange(newCursorPos, newCursorPos)
    }

    const errorEl = document.getElementById('presential-vip-phone-error')
    const sendBtn = document.getElementById('presential-send-btn')
    const isComplete = digits.length === 11
    const validCell = isComplete && digits[2] === '9'

    if (errorEl) errorEl.classList.toggle('hidden', !isComplete || validCell)
    if (errorEl && isComplete && !validCell) {
      errorEl.textContent = 'Número inválido — precisa ser um celular (começando com 9).'
    }

    // Habilita botão enviar quando o telefone é válido
    if (sendBtn && validCell) {
      // lookup em andamento — não altera o estado do botão
    }

    // Consulta o endpoint ao completar 11 dígitos válidos
    clearTimeout(this._presentialVipLookupTimeout)
    if (validCell) {
      this._presentialVipLookupTimeout = setTimeout(() => {
        this._lookupPresentialVip(input.value)
      }, 400)
    }
  }

  async _lookupPresentialVip(phone) {
    const companySlug = this.companySlugValue || window.VIPTableMenu?.companySlug
    if (!companySlug) return
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content || ''
    try {
      const resp = await fetch(`/v2/companies/${companySlug}/clientship_by_phone`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
        body: JSON.stringify({ phone })
      })
      if (resp.ok) {
        const data = await resp.json()
        if (data.vip_member) {
          this._flipVipCardToWelcome(data.name, data.uuid)
        } else {
          this._flipVipCardToRegistration(phone, data)
        }
      } else if (resp.status === 404) {
        this._flipVipCardToRegistration(phone, null)
      }
    } catch (_e) {
      // erro de rede — ignora silenciosamente
    }
  }

  _flipVipCard(contentHtml) {
    const scene   = document.getElementById('vip-card-scene')
    const flipEl  = document.getElementById('vip-card-flip')
    const front   = document.getElementById('vip-card-front')
    const back    = document.getElementById('vip-card-back')
    if (!scene || !flipEl || !front || !back) return

    back.innerHTML = contentHtml
    flipEl.style.minHeight = front.offsetHeight + 'px'
    back.style.visibility = 'visible'
    flipEl.style.transform = 'rotateY(180deg)'

    setTimeout(() => {
      flipEl.style.minHeight = back.offsetHeight + 'px'
    }, 750)
  }

  flipVipCardToFront() {
    const flipEl = document.getElementById('vip-card-flip')
    const front  = document.getElementById('vip-card-front')
    const back   = document.getElementById('vip-card-back')
    if (!flipEl) return

    flipEl.style.transform = 'rotateY(0deg)'

    // após a transição: esconde o verso e restaura a altura
    setTimeout(() => {
      if (back) back.style.visibility = 'hidden'
      if (front) flipEl.style.minHeight = front.offsetHeight + 'px'
    }, 750)

    // limpa o telefone digitado e recoloca foco para correção
    const phoneInput = document.getElementById('presential-vip-phone')
    if (phoneInput) { phoneInput.value = ''; phoneInput.focus() }

    // ao voltar: habilita enviar como cliente normal
    const sendBtn = document.getElementById('presential-send-btn')
    if (sendBtn) {
      sendBtn.disabled = false
      sendBtn.style.opacity = '1'
      sendBtn.style.cursor = 'pointer'
      sendBtn.textContent = 'Enviar pedido como cliente normal'
    }
    this._setFooterNormalMode()
  }

  skipVipPresential() {
    // Usu\u00e1rio optou por n\u00e3o se identificar — libera o bot\u00e3o "Enviar pedido" diretamente
    const sendBtn = document.getElementById('presential-send-btn')
    if (sendBtn) {
      sendBtn.disabled = false
      sendBtn.style.opacity = '1'
      sendBtn.style.cursor = 'pointer'
      sendBtn.textContent = 'Enviar pedido'
    }
    this._setFooterNormalMode()
    const skipBtn = document.getElementById('presential-skip-vip-btn')
    if (skipBtn) skipBtn.style.display = 'none'
  }

  _setFooterVipMode() {
    document.getElementById('presential-footer')?.classList.add('footer-vip-mode')
    document.getElementById('presential-send-btn')?.classList.add('btn-vip-mode')
  }

  _setFooterNormalMode() {
    document.getElementById('presential-footer')?.classList.remove('footer-vip-mode')
    document.getElementById('presential-send-btn')?.classList.remove('btn-vip-mode')
  }

  claimVipPresential() {
    // Usu\u00e1rio clicou em "Quero ser VIP" no card frontal — abre o formul\u00e1rio de cadastro
    const phone = document.getElementById('presential-vip-phone')?.value || ''
    this._flipVipCardToRegistration(phone, null)
  }

  _flipVipCardToWelcome(name, uuid) {
    this._presentialClientUuid = uuid
    this._presentialClientName = name || ''

    // cliente identificado: habilita "Enviar pedido"
    const sendBtn = document.getElementById('presential-send-btn')
    if (sendBtn) {
      sendBtn.disabled = false
      sendBtn.style.opacity = '1'
      sendBtn.style.cursor = 'pointer'
      sendBtn.textContent = 'Enviar pedido como cliente VIP 👑'
    }
    this._setFooterVipMode()
    const skipBtn = document.getElementById('presential-skip-vip-btn')
    if (skipBtn) skipBtn.style.display = 'none'
    const firstName = (name || '').split(' ')[0]
    const html = `
      <div class="w-full h-1 rounded-full mb-5" style="background:linear-gradient(90deg,transparent,#D4AF37,#F4E4A6,#D4AF37,transparent);"></div>
      <div class="flex items-center gap-4 mb-5">
        <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 64 64" fill="none" class="flex-shrink-0">
          <path d="M8 44 L8 48 L56 48 L56 44 Z" fill="#D4AF37"/>
          <path d="M8 44 L14 20 L24 34 L32 14 L40 34 L50 20 L56 44 Z" fill="#D4AF37"/>
          <circle cx="32" cy="14" r="3" fill="#F4E4A6"/>
          <circle cx="14" cy="20" r="2.5" fill="#F4E4A6"/>
          <circle cx="50" cy="20" r="2.5" fill="#F4E4A6"/>
        </svg>
        <div>
          <p style="color:rgba(212,175,55,0.6);font-size:0.7rem;letter-spacing:0.1em;text-transform:uppercase;font-family:'Lato',sans-serif;">Cliente VIP</p>
          <h3 style="font-family:'Playfair Display',serif;font-size:1.75rem;font-weight:700;background:linear-gradient(90deg,#B8962E 0%,#D4AF37 20%,#ffffff 45%,#F4E4A6 55%,#D4AF37 75%,#B8962E 100%);background-size:400% auto;-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;animation:vipTitleShimmer 5s ease-in-out infinite;">Olá, ${this.escapeHtml(firstName)}!</h3>
        </div>
      </div>
      <p style="color:rgba(255,255,255,0.9);font-size:1rem;font-weight:500;margin-bottom:0.5rem;">Bom te ver de novo 👑</p>
      <p style="color:rgba(212,175,55,0.65);font-size:0.875rem;">Você está identificado como cliente VIP e tem acesso a benefícios exclusivos!</p>
    `
    this._flipVipCard(html)
  }

  _flipVipCardToRegistration(phone, clientData) {
    const crownSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 64 64" fill="none" style="flex-shrink:0"><path d="M8 44 L8 48 L56 48 L56 44 Z" fill="#D4AF37"/><path d="M8 44 L14 20 L24 34 L32 14 L40 34 L50 20 L56 44 Z" fill="#D4AF37"/><circle cx="32" cy="14" r="3" fill="#F4E4A6"/><circle cx="14" cy="20" r="2.5" fill="#F4E4A6"/><circle cx="50" cy="20" r="2.5" fill="#F4E4A6"/></svg>`
    const labelStyle = `color:rgba(212,175,55,0.6);font-size:0.7rem;letter-spacing:0.08em;text-transform:uppercase;font-family:'Lato',sans-serif;display:block;margin-bottom:0.25rem;`
    const html = `
      <div class="w-full h-1 rounded-full mb-4" style="background:linear-gradient(90deg,transparent,#D4AF37,#F4E4A6,#D4AF37,transparent);"></div>
      <button type="button"
        style="display:inline-flex;align-items:center;gap:0.5rem;margin-bottom:1rem;font-size:0.82rem;font-weight:500;color:rgba(212,175,55,0.9);background:rgba(212,175,55,0.1);border:1px solid rgba(212,175,55,0.3);padding:0.35rem 0.85rem;border-radius:999px;cursor:pointer;transition:all 0.2s;"
        data-action="click->companies--v2--menu#flipVipCardToFront"
        onmouseover="this.style.background='rgba(212,175,55,0.2)';this.style.borderColor='rgba(212,175,55,0.6)';this.style.color='#D4AF37'"
        onmouseout="this.style.background='rgba(212,175,55,0.1)';this.style.borderColor='rgba(212,175,55,0.3)';this.style.color='rgba(212,175,55,0.9)'">
        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/></svg>
        Cancelar
      </button>
      <div style="display:flex;align-items:center;gap:0.75rem;margin-bottom:0.75rem;">
        ${crownSvg}
        <h3 style="font-family:'Playfair Display',serif;font-size:1.4rem;font-weight:700;background:linear-gradient(90deg,#B8962E 0%,#D4AF37 25%,#ffffff 48%,#D4AF37 72%,#B8962E 100%);background-size:400% auto;-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;animation:vipTitleShimmer 5s ease-in-out infinite;">Seja um cliente VIP</h3>
      </div>
      <p style="color:rgba(212,175,55,0.65);font-size:0.875rem;margin-bottom:1.25rem;">Preencha seus dados para acessar benefícios exclusivos.</p>
      <div style="display:flex;flex-direction:column;gap:1rem;margin-bottom:1.25rem;">
        <div>
          <label style="${labelStyle}">Nome completo</label>
          <input id="pr-name" type="text" autocomplete="name" placeholder="Seu nome completo" value="${this.escapeHtml(clientData?.name || '')}"
            data-action="input->companies--v2--menu#_validatePresentialRegistrationForm">
        </div>
        <div>
          <label style="${labelStyle}">Telefone</label>
          <input id="pr-phone" type="tel" autocomplete="tel" placeholder="(99) 99999-9999"
            data-action="input->companies--v2--menu#onPresentialRegPhoneInput"
            value="${this.escapeHtml(phone)}">
        </div>
        <div>
          <label style="${labelStyle}">E-mail</label>
          <input id="pr-email" type="email" autocomplete="email" placeholder="seu@email.com"
            data-action="input->companies--v2--menu#_validatePresentialRegistrationForm">
        </div>
        <div>
          <label style="${labelStyle}">Data de nascimento</label>
          <input id="pr-birthday" type="text" inputmode="numeric" maxlength="10" placeholder="DD/MM/AAAA"
            data-action="input->companies--v2--menu#onPresentialBirthdayInput">
        </div>
      </div>
      <div style="display:flex;flex-direction:column;gap:0.625rem;margin-bottom:1.25rem;">
        <label style="display:flex;align-items:flex-start;gap:0.75rem;cursor:pointer;">
          <input id="pr-accept-comm" type="checkbox" style="margin-top:0.125rem;flex-shrink:0;accent-color:#D4AF37;">
          <span style="color:rgba(255,255,255,0.7);font-size:0.8rem;">Aceito receber ofertas e comunicação exclusiva</span>
        </label>
        <label style="display:flex;align-items:flex-start;gap:0.75rem;cursor:pointer;">
          <input id="pr-terms" type="checkbox" style="margin-top:0.125rem;flex-shrink:0;accent-color:#D4AF37;"
            data-action="change->companies--v2--menu#onPresentialTermsChange">
          <span style="color:rgba(255,255,255,0.7);font-size:0.8rem;">Li e aceito os <a href="/por-tag/for_users" target="_blank" style="color:#D4AF37;text-decoration:underline;">Termos e políticas de uso</a></span>
        </label>
      </div>
      <p id="pr-submit-error" style="display:none;margin-top:0.5rem;font-size:0.75rem;color:#f87171;"></p>
    `
    this._flipVipCard(html)

    // enquanto o formul\u00e1rio estiver aberto, o "Enviar pedido" reflete a a\u00e7\u00e3o dupla
    const sendBtn = document.getElementById('presential-send-btn')
    if (sendBtn) {
      sendBtn.disabled = true
      sendBtn.style.opacity = '0.4'
      sendBtn.style.cursor = 'not-allowed'
      sendBtn.textContent = 'Enviar pedido e entrar para o clube VIP'
    }
    this._setFooterVipMode()
  }

  onPresentialRegPhoneInput(event) {
    const input = event.target
    const digits = (input.value || '').replace(/\D/g, '').slice(0, 11)
    const a = digits.slice(0, 2)
    const b = digits.slice(2, 7)
    const c = digits.slice(7, 11)
    let out = ''
    if (a.length) out += `(${a}` + (a.length === 2 ? ') ' : '')
    if (b.length) out += b
    if (c.length) out += `-${c}`
    input.value = out
    this._validatePresentialRegistrationForm()
  }

  onPresentialBirthdayInput(event) {
    const input = event.target
    let v = input.value.replace(/\D/g, '').slice(0, 8)
    if (v.length >= 5)      v = v.slice(0, 2) + '/' + v.slice(2, 4) + '/' + v.slice(4)
    else if (v.length >= 3) v = v.slice(0, 2) + '/' + v.slice(2)
    input.value = v
    this._validatePresentialRegistrationForm()
  }

  onPresentialTermsChange() {
    this._validatePresentialRegistrationForm()
  }

  _validatePresentialRegistrationForm() {
    const name     = document.getElementById('pr-name')?.value.trim()
    const phone    = (document.getElementById('pr-phone')?.value || '').replace(/\D/g, '')
    const email    = document.getElementById('pr-email')?.value.trim()
    const birthday = document.getElementById('pr-birthday')?.value
    const terms    = document.getElementById('pr-terms')?.checked
    const sendBtn   = document.getElementById('presential-send-btn')
    const validPhone = phone.length === 11 && phone[2] === '9'
    const ok = !!(name && validPhone && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email || '') && /^\d{2}\/\d{2}\/\d{4}$/.test(birthday || '') && terms)
    // "Enviar pedido" s\u00f3 habilitado quando todos os dados do formul\u00e1rio estiverem v\u00e1lidos
    if (sendBtn) {
      sendBtn.disabled = !ok
      sendBtn.style.opacity = ok ? '1' : '0.4'
      sendBtn.style.cursor = ok ? 'pointer' : 'not-allowed'
      sendBtn.textContent = 'Enviar pedido e entrar para o clube VIP'
    }
  }

  async submitPresentialRegistration() {
    const phone    = document.getElementById('pr-phone')?.value
    const name     = document.getElementById('pr-name')?.value.trim()
    const email    = document.getElementById('pr-email')?.value.trim()
    const birthday = document.getElementById('pr-birthday')?.value
    const acceptComm = document.getElementById('pr-accept-comm')?.checked
    const errorEl  = document.getElementById('pr-submit-error')
    const sendBtn  = document.getElementById('presential-send-btn')

    // DD/MM/YYYY → YYYY-MM-DD
    const [dd, mm, yyyy] = (birthday || '').split('/')
    const birthdayIso = yyyy && mm && dd ? `${yyyy}-${mm}-${dd}` : null

    const csrf = document.querySelector('meta[name="csrf-token"]')?.content || ''
    try {
      const resp = await fetch(`/clientes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf, 'Accept': 'application/json' },
        body: JSON.stringify({ clientship: {
          phone, name, email,
          birthday:              birthdayIso,
          accept_communication:  acceptComm ? '1' : '0',
          terms_and_conditions:  '1'
        }})
      })
      if (resp.ok) {
        const data = await resp.json()
        this._flipVipCardToWelcome(name, data.uuid)
        return true
      } else {
        const msg = 'Erro ao salvar cadastro. Tente novamente.'
        if (errorEl) { errorEl.textContent = msg; errorEl.style.display = 'block' }
        if (sendBtn) { sendBtn.disabled = false; sendBtn.style.opacity = '1'; sendBtn.style.cursor = 'pointer'; sendBtn.textContent = 'Enviar pedido e entrar para o clube VIP' }
        return false
      }
    } catch (_e) {
      if (errorEl) { errorEl.textContent = 'Erro de conex\u00e3o. Tente novamente.'; errorEl.style.display = 'block' }
      if (sendBtn) { sendBtn.disabled = false; sendBtn.style.opacity = '1'; sendBtn.style.cursor = 'pointer'; sendBtn.textContent = 'Enviar pedido e entrar para o clube VIP' }
      return false
    }
  }

  async persistAndSendPresential(event) {

    const sendBtn = document.getElementById('presential-send-btn')
    const originalBtnText = sendBtn?.textContent

    const isRegistrationOpen = !!document.getElementById('pr-name')
    if (isRegistrationOpen) {
      if (sendBtn) {
        sendBtn.disabled = true
        sendBtn.style.opacity = '0.7'
        sendBtn.style.cursor = 'not-allowed'
        sendBtn.textContent = 'Criando conta VIP...'
      }
      const success = await this.submitPresentialRegistration()
      if (!success) {
        if (waWindow && !waWindow.closed) waWindow.close()
        return
      }
      // submitPresentialRegistration j\u00e1 atualizou o card e o uuid — prosseguir com envio
    }

    if (!this.cart || this.cart.length === 0) {
      this.showToast('Carrinho vazio — adicione itens antes de enviar', 'warning')
      if (waWindow && !waWindow.closed) waWindow.close()
      return
    }

    if (sendBtn) {
      sendBtn.disabled = true
      sendBtn.style.opacity = '0.7'
      sendBtn.style.cursor = 'not-allowed'
      sendBtn.textContent = 'Enviando pedido...'
    }

    let orderSucceeded = false
    try {
      const companySlug = this.companySlugValue || window.VIPTableMenu?.companySlug
      const csrf = document.querySelector('meta[name="csrf-token"]')?.content || ''

      // Monta payload de itens no formato legado esperado por Order#parse_original_json_order_into_object
      const legacyOrders = this.cart.map(item => {
        // price.price deve ser o preço BASE (sem adicionais), pois OrdersFood#total_price
        // adiciona os adicionais em cima dele. Usar item.price (preço final) causaria
        // dupla contagem dos adicionais no painel do gerente.
        //
        // Itens com price_data (adicionados pelo código atual): usa price_data.price diretamente.
        // Itens sem price_data (localStorage antigo, antes do fix): deriva o base subtraindo
        // os adicionais de item.price para evitar double-count em produção.
        let basePrice
        if (item.price_data?.price != null) {
          basePrice = parseInt(item.price_data.price, 10)
        } else {
          const additionalsSum = (item.additionals || []).reduce((s, a) => s + parseInt(a.price || 0, 10), 0)
          basePrice = parseInt(item.price, 10) - additionalsSum
          if (additionalsSum > 0) {
            console.warn('[VIPTable] Legacy cart item detected (no price_data) — derived basePrice:', {
              itemName: item.name, itemPrice: item.price, additionalsSum, basePrice
            })
          }
        }
        return [{
        food_informations: { id: parseInt(item.id, 10), name: item.name },
        price: { price: basePrice, text: item.price_data?.text || '' },
        pizza: item.pizza === 'true' || item.pizza === true,
        observations: item.observations || '',
        optionals: item.optionals || [],
        additionals: item.additionals || [],
        flavours: item.flavours || [],
        quantity: item.quantity,
        final_price: parseInt(item.price, 10) * item.quantity
      }]})

      // Telefone do campo do cartão VIP (frente); remove máscara
      const rawPhone = document.getElementById('presential-vip-phone')?.value || ''
      const phoneDigits = rawPhone.replace(/\D/g, '')
      const clientName = this._presentialClientName || ''
      const vipcodeId  = document.getElementById('presential-vipcode-id')?.value || ''

      // Monta payload base — vipcode_id é obrigatório para pedidos presenciais
      const orderPayload = {
        original_json_order: JSON.stringify(legacyOrders),
        deliver_method: 'take_away',
        vipcode_id: vipcodeId
      }

      // Inclui dados do cliente apenas se o cliente se identificou (tem telefone)
      if (phoneDigits) {
        orderPayload.clientship_attributes = { phone: phoneDigits, name: clientName }
      }

      const resp = await fetch(`/v2/companies/${companySlug}/orders`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf, 'Accept': 'application/json' },
        body: JSON.stringify({ order: orderPayload })
      })

      if (resp.status === 409) {
        const conflict = await resp.json().catch(() => ({}))
        if (conflict.code === 'prices_changed') {
          this.applyServerPriceCorrections(conflict)
          return
        }
      }

      if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({}))
        throw new Error(errorData.message || `Erro ao criar pedido (${resp.status})`)
      }

      const orderData = await resp.json()
      if (!orderData.success) throw new Error(orderData.message || 'Erro ao criar pedido')

      // Pedido presencial: não abre WhatsApp — redireciona direto para tela de sucesso

      // (bloco WhatsApp abaixo nunca é atingido no fluxo presencial) —
      // mantido desativado com flag para reuso futuro se necessário
      const restaurantWhatsApp = null
      if (restaurantWhatsApp) {
        const SEP = '\n'
        let message = 'Ol\u00e1, gostaria de fazer o seguinte pedido:'
        message += `${SEP}\n\u{1F6D2} *ITENS DO PEDIDO*${SEP}`
        this.cart.forEach(item => {
          const itemTotal = this.formatCurrency(parseInt(item.price, 10) * parseInt(item.quantity, 10))
          const variation = item.price_data?.text ? ` _(${item.price_data.text})_` : ''
          message += `\n\n*${item.quantity}x ${item.name}*${variation} \u2014 ${itemTotal}`
          if (item.flavours?.length) {
            message += `\n  \u{1F355} Sabores:`
            item.flavours.forEach(f => { message += `\n    \u2022 ${f.name || f.text || ''}` })
          }
          if (item.additionals?.length) {
            message += `\n  Adicionais:`
            item.additionals.forEach(a => {
              const price = parseInt(a.price, 10)
              message += `\n    \u2022 ${a.name || a.text || ''}${price > 0 ? ' \u2014 ' + this.formatCurrency(price) : ''}`
            })
          }
          if (item.optionals?.length) {
            message += `\n  \u{1F6AB} Sem:`
            item.optionals.forEach(o => { message += `\n    \u2022 ${o.name || o.text || ''}` })
          }
          if (item.observations?.trim()) message += `\n  \u{1F4DD} Obs: ${item.observations.trim()}`
        })

        if (clientName || rawPhone) {
          message += `\n${SEP}\n\u{1F464} *DADOS DO CLIENTE*${SEP}`
          if (clientName) message += `\n Nome: ${clientName}`
          if (rawPhone)   message += `\n Telefone: ${rawPhone}`
        }

        message += `\n${SEP}\n\u{1F3EA} *RETIRADA NO BALC\u00C3O*${SEP}`

        const subtotalCents = this.cart.reduce((s, it) => s + (parseInt(it.price, 10) * parseInt(it.quantity, 10)), 0)
        message += `\n${SEP}\n *TOTAL: ${this.formatCurrency(subtotalCents)}*`

        if (orderData.check_link) message += `\n\n\u{1F517} _Painel do estabelecimento:_\n_${orderData.check_link}_`

        const waLink = `https://wa.me/${this._waPhone(restaurantWhatsApp)}?text=${encodeURIComponent(message)}`
        localStorage.setItem('viptable_pending_whatsapp', JSON.stringify({ phone: this._waPhone(restaurantWhatsApp), message }))
        window.location.href = waLink
      }

      // Limpar carrinho e redirecionar para página de sucesso
      this.cart = []
      this.saveCart()
      this.updateCartUI()
      window.dispatchEvent(new CustomEvent('viptable:cart:cleared', { detail: { company: companySlug } }))

      orderSucceeded = true
      window.location.href = `/v2/companies/${companySlug}/orders/${orderData.order.uuid}/success`
    } catch (err) {
      console.error('[VIPTable Menu] persistAndSendPresential error', err)
      this.showToast(err.message || 'Erro ao enviar pedido. Tente novamente.', 'error')
    } finally {
      if (!orderSucceeded && sendBtn) {
        sendBtn.disabled = false
        sendBtn.style.opacity = '1'
        sendBtn.style.cursor = 'pointer'
        sendBtn.textContent = originalBtnText || 'Enviar pedido'
      }
    }
  }

  renderCartItems() {
    if (!this.hasCartContainerTarget) return

    if (this.cart.length === 0) {
      this.cartContainerTarget.innerHTML = `
        <div class="empty-cart text-center py-12">
          <svg class="w-16 h-16 mx-auto mb-4"  xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"/>
          </svg>
          <p >Seu carrinho está vazio</p>
          <p class="text-sm mt-1" >Adicione itens deliciosos ao seu pedido</p>
        </div>
      `
      return
    }

    const itemsHtml = this.cart.map((item, idx) => `
      <div class="cart-item flex items-center gap-4 p-4 rounded-xl mb-3"  data-food-id="${item.id}">
        <div class="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 border border-white border-opacity-10">
          ${(() => {
            const domImg = document.querySelector(`[data-food-id="${item.id}"] img`)
            const imageSrc = (item.image && item.image.toString().trim()) ? item.image : (domImg ? domImg.src : '/fallbacks/food.jpg')
            if (!item.image || !item.image.toString().trim()) console.debug('[VIPTable Menu] Using DOM/default image for cart item (stimulus):', { id: item.id, domImgSrc: domImg ? domImg.src : null })
            return `<img src="${imageSrc}" alt="${this.escapeHtml(item.name)}" class="w-full h-full object-cover">`
          })()}
        </div>
        
        <div class="flex-1 min-w-0">
          <h4 class="font-medium text-sm" >${this.escapeHtml(item.name)}</h4>
          ${(item.additionals && item.additionals.length > 0) ? `<div class="mt-1 space-y-0.5">${item.additionals.map(a => `<p class="text-xs opacity-60 pl-2">+ ${this.escapeHtml(a.text)}${a.price ? ` (${this.formatCurrency(a.price)})` : ''}</p>`).join('')}</div>` : ''}
          ${(item.optionals && item.optionals.length > 0) ? `<div class="mt-1 space-y-0.5">${item.optionals.map(o => `<p class="text-xs opacity-60 pl-2">• ${this.escapeHtml(o.text)}</p>`).join('')}</div>` : ''}
          ${(item.flavours && item.flavours.length > 0) ? `<div class="mt-1 space-y-0.5">${item.flavours.map(f => `<p class="text-xs opacity-60 pl-2">🍕 ${this.escapeHtml(f.text)}</p>`).join('')}</div>` : ''}
          ${item.observations ? `<p class="text-xs opacity-50 mt-1 pl-2">Obs: ${this.escapeHtml(item.observations)}</p>` : ''}
          <p class="font-semibold mt-1" >${this.formatCurrency(item.price * item.quantity)}</p>
        </div>
        
        <div class="flex items-center gap-2">
          <button 
            class="w-8 h-8 rounded-full bg-white bg-opacity-10 flex items-center justify-center hover:bg-opacity-20 transition-colors"
            data-food-id="${item.id}"
            data-cart-index="${idx}"
            data-action="click->companies--v2--menu#decrementQuantity"
          >
            <svg class="w-4 h-4"  xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 12H4"/>
            </svg>
          </button>
          
          <span class="w-8 text-center font-medium">${item.quantity}</span>
          
          <button 
            class="w-8 h-8 rounded-full flex items-center justify-center transition-colors"
            data-food-id="${item.id}"
            data-cart-index="${idx}"
            data-action="click->companies--v2--menu#incrementQuantity"
          >
            <svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
            </svg>
          </button>
        </div>
      </div>
    `).join('')

    this.cartContainerTarget.innerHTML = itemsHtml
  }



  /**
   * Cancela adição de novo endereço e volta para lista
   */
  cancelAddressForm(event) {
    event.preventDefault()
    const newAddressForm = document.getElementById('checkout-new-address-form')
    const addressesSection = document.getElementById('checkout-addresses-section')
    const errorMessage = document.getElementById('cep-error-message')
    
    // Esconder mensagem de erro se estiver visível
    if (errorMessage) {
      errorMessage.style.display = 'none'
    }
    
    if (newAddressForm) {
      // Esconder formulário com animação
      newAddressForm.style.maxHeight = '0'
      newAddressForm.style.opacity = '0'
      setTimeout(() => {
        newAddressForm.classList.add('hidden')
      }, 300)
    }
    
    // Mostrar lista de endereços novamente
    if (addressesSection) {
      addressesSection.classList.remove('hidden')
      addressesSection.offsetHeight
      addressesSection.style.maxHeight = '600px'
      addressesSection.style.opacity = '1'
      
      // Selecionar o primeiro endereço e recalcular taxa
      setTimeout(() => {
        const firstAddressCard = addressesSection.querySelector('.address-card')
        if (firstAddressCard) {
          // Simular clique no primeiro endereço
          firstAddressCard.click()
          console.log('[VIPTable Menu] First address reselected after cancel')
        }
      }, 350)
    }
  }

  /**
   * Inicializa máscara e eventos do campo CEP
   */
  setupCepField() {
    const cepInput = this.getVisibleAddressField('order_clientship_address_zipcode')
    if (!cepInput || cepInput._hasSetup) return
    
    // Máscara para CEP (XXXXX-XXX)
    cepInput.addEventListener('input', (e) => {
      let value = e.target.value.replace(/\D/g, '')
      if (value.length > 8) value = value.slice(0, 8)
      
      if (value.length > 5) {
        value = value.slice(0, 5) + '-' + value.slice(5)
      }
      
      e.target.value = value
      
      // Quando 8 dígitos completos, buscar CEP
      if (value.replace(/\D/g, '').length === 8) {
        this.fetchCepData(value.replace(/\D/g, ''))
      }
    })
    
    cepInput._hasSetup = true
  }

  /**
   * Busca dados do CEP e preenche os campos
   */
  async fetchCepData(cep) {
    if (cep.length !== 8) return
    
    const errorMessage = this.getVisibleAddressField('cep-error-message')
                       || document.getElementById('cep-error-message')
    
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`)
      const data = await response.json()
      
      if (data.erro) {
        console.error('[VIPTable Menu] CEP não encontrado')
        if (errorMessage) {
          errorMessage.textContent = 'CEP não encontrado. Por favor, confira se o CEP está correto.'
          errorMessage.style.display = 'block'
        }
        return
      }
      
      // Esconder mensagem de erro se estava visível
      if (errorMessage) {
        errorMessage.style.display = 'none'
      }
      
      // Preencher campos (usar o campo VISÍVEL, não o primeiro com ID duplicado)
      const cityField = this.getVisibleAddressField('order_clientship_address_city_name')
      const stateField = this.getVisibleAddressField('order_clientship_address_state_abbreviation')
      const neighborhoodField = this.getVisibleAddressField('order_clientship_address_neighborhood')
      const streetField = this.getVisibleAddressField('order_clientship_address_street')
      
      if (cityField) cityField.value = data.localidade || ''
      if (stateField) stateField.value = data.uf || ''
      if (neighborhoodField) neighborhoodField.value = data.bairro || ''
      if (streetField) streetField.value = data.logradouro || ''

      // CEPs terminados em 000 (zona rural/geral) frequentemente não retornam bairro/rua.
      // Nesses casos, habilitar os campos para preenchimento manual.
      const cepEndsIn000 = cep.endsWith('000')
      if (neighborhoodField) {
        if (cepEndsIn000 && !data.bairro) {
          neighborhoodField.removeAttribute('readonly')
        } else {
          neighborhoodField.setAttribute('readonly', 'readonly')
        }
      }
      if (streetField) {
        if (cepEndsIn000 && !data.logradouro) {
          streetField.removeAttribute('readonly')
        } else {
          streetField.setAttribute('readonly', 'readonly')
        }
      }
      
      // Mostrar campos adicionais com animação
      this.showAddressFields()

      // Após revelar os campos, registrar listeners de frete/validação e focar no número
        setTimeout(() => {
          this.setupAddressFieldListeners()
          this.validateSendButton()
          const neighborhoodField = this.getVisibleAddressField('order_clientship_address_neighborhood')
          const streetNumberField = this.getVisibleAddressField('order_clientship_address_street_number')
          // Se bairro está editável e vazio, foca nele; senão, foca no número
          if (neighborhoodField && !neighborhoodField.hasAttribute('readonly') && neighborhoodField.value.trim() === '') {
            neighborhoodField.focus()
          } else if (streetNumberField) {
            streetNumberField.focus()
          }
        }, 350)
      
    } catch (error) {
      console.error('[VIPTable Menu] Erro ao buscar CEP:', error)
      if (errorMessage) {
        errorMessage.textContent = 'Erro ao buscar CEP. Verifique sua conexão e tente novamente.'
        errorMessage.style.display = 'block'
      }
    }
  }

  /**
   * Mostra campos adicionais do endereço com animação
   */
  showAddressFields() {
    const additionalFields = this.getVisibleAddressField('address-additional-fields')
                          || document.getElementById('address-additional-fields')
    if (!additionalFields) return
    
    additionalFields.classList.remove('hidden')
    // Force reflow
    additionalFields.offsetHeight
    // Usar 600px fixo (scrollHeight retorna 0 em elementos nunca visíveis)
    additionalFields.style.maxHeight = additionalFields.scrollHeight > 0
      ? additionalFields.scrollHeight + 'px'
      : '600px'
    additionalFields.style.opacity = '1'
    additionalFields.style.overflow = 'visible'
  }

  /**
   * Adiciona listeners de blur/input nos campos de endereço visíveis:
   * - blur no número → calcula frete (se CEP + número preenchidos)
   * - input no CEP, número e identificação → revalida botão de envio
   * Pode ser chamado múltiplas vezes com segurança (guards _has*Handler).
   */
  setupAddressFieldListeners() {
    const zipcodeField     = this.getVisibleAddressField('order_clientship_address_zipcode')
    const streetNumberField = this.getVisibleAddressField('order_clientship_address_street_number')
    const nameField        = this.getVisibleAddressField('order_clientship_address_name')
      const neighborhoodField = this.getVisibleAddressField('order_clientship_address_neighborhood')
      const streetField = this.getVisibleAddressField('order_clientship_address_street')

    // blur no número → calcular frete
    if (streetNumberField && !streetNumberField._hasBlurHandler) {
      streetNumberField.addEventListener('blur', () => {
        const hasZipcode = zipcodeField && zipcodeField.value.replace(/\D/g, '').length === 8
        const hasNumber  = streetNumberField.value.trim() !== ''
        if (hasZipcode && hasNumber) this.calculateDeliveryRate()
      })
      streetNumberField._hasBlurHandler = true
    }

    // input em qualquer campo → revalidar botão
    if (zipcodeField && !zipcodeField._hasInputValidation) {
      zipcodeField.addEventListener('input', () => this.validateSendButton())
      zipcodeField._hasInputValidation = true
    }
    if (streetNumberField && !streetNumberField._hasInputValidation) {
      streetNumberField.addEventListener('input', () => this.validateSendButton())
      streetNumberField._hasInputValidation = true
    }
    if (nameField && !nameField._hasInputValidation) {
      nameField.addEventListener('input', () => this.validateSendButton())
      nameField._hasInputValidation = true
    }
      if (neighborhoodField && !neighborhoodField._hasInputValidation) {
        neighborhoodField.addEventListener('input', () => this.validateSendButton())
        neighborhoodField._hasInputValidation = true
      }
      if (streetField && !streetField._hasInputValidation) {
        streetField.addEventListener('input', () => this.validateSendButton())
        streetField._hasInputValidation = true
      }
  }

  /**
   * Calcula a taxa de entrega baseado no endereço (existente ou novo)
   * Replica a lógica do Menu.Order.loadCoordinates() da V1
   */
  async calculateDeliveryRate() {
    const companySlug = this.companySlugValue
    if (!companySlug) {
      console.error('[VIPTable Menu] Company slug not found')
      return
    }

    const url = `/companies/${companySlug}/orders/calculate_delivery_rate`
    
    // CSRF token is required and cannot be empty
    const csrfTokenElement = document.querySelector('[name="csrf-token"]')
    if (!csrfTokenElement) {
      console.error('[VIPTable Menu] Security: CSRF token not found. Request blocked.')
      return
    }
    
    const authenticityToken = csrfTokenElement.content
    if (!authenticityToken || authenticityToken === '') {
      console.error('[VIPTable Menu] Security: CSRF token is empty. Request blocked.')
      return
    }

    // Montar dados do endereço
    let addressData
    const addressUuidInput = document.getElementById('order_clientship_address_id')
    
    if (addressUuidInput && addressUuidInput.value) {
      // Endereço existente - enviar apenas UUID
      addressData = {
        authenticity_token: authenticityToken,
        clientship_address: {
          uuid: addressUuidInput.value
        }
      }
    } else {
      // Novo endereço - enviar todos os campos
      const zipcodeField = this.getVisibleAddressField('order_clientship_address_zipcode')
      const streetField = this.getVisibleAddressField('order_clientship_address_street') || document.querySelector('.street')
      const streetNumberField = this.getVisibleAddressField('order_clientship_address_street_number') || document.querySelector('.street_number')
      const cityField = this.getVisibleAddressField('order_clientship_address_city_name') || document.querySelector('.city_name')
      const stateField = this.getVisibleAddressField('order_clientship_address_state_abbreviation') || document.querySelector('.state')
      const neighborhoodField = this.getVisibleAddressField('order_clientship_address_neighborhood') || document.querySelector('.neighborhood')
      
      addressData = {
        authenticity_token: authenticityToken,
        clientship_address: {
          zipcode: zipcodeField?.value || '',
          street: streetField?.value || '',
          street_number: streetNumberField?.value || '',
          city_name: cityField?.value || '',
          state_abbreviation: stateField?.value || '',
          neighborhood: neighborhoodField?.value || ''
        }
      }
    }

    console.log('[VIPTable Menu] Calculating delivery rate...', addressData)

    // Serializar dados aninhados para o formato que Rails espera
    const formData = new URLSearchParams()
    formData.append('authenticity_token', authenticityToken)
    
    if (addressData.clientship_address) {
      Object.keys(addressData.clientship_address).forEach(key => {
        formData.append(`clientship_address[${key}]`, addressData.clientship_address[key])
      })
    }

    console.log('[VIPTable Menu] Form data serialized:', formData.toString())

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Accept': 'application/json',
          'X-CSRF-Token': authenticityToken,
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      })

      if (response.status === 429) {
        alert('Muitas requisições. Por favor, tente novamente em alguns instantes.')
        return
      }
      
      if (response.status === 403) {
        alert('Erro de segurança. Por favor, recarregue a página.')
        location.reload()
        return
      }

      if (!response.ok) {
        console.error('[VIPTable Menu] Error calculating delivery rate:', response.status)
        return
      }

      const data = await response.json()

      const rateValue = parseFloat(data.delivery_rate) || 0

      if (data.calc_method === 'different_city') {
        console.warn('[VIPTable Menu] Address city differs from restaurant city — fee to be informed by attendant')
        this._showDifferentCityNotice()
      } else {
        this._clearDifferentCityNotice()
      }

      console.log('[VIPTable Menu] Delivery rate from server:', data)

      // Atualizar campo hidden legado para compatibilidade com submit do form
      const deliveryRateField = document.getElementById('delivery_rate_field') || document.getElementById('order_delivery_rate')
      if (deliveryRateField) deliveryRateField.value = rateValue

      const distanceField = document.getElementById('distance_in_km')
      if (distanceField) distanceField.value = data.distance_km || ''

      const methodCalcField = document.getElementById('delivery_method_calc')
      if (methodCalcField) methodCalcField.value = data.calc_method || ''

      // Atualizar UI da taxa de entrega (atualiza this.deliveryRateValue e chama updateTotalPrice())
      this.updateDeliveryRateUI(rateValue)

      console.log('[VIPTable Menu] Delivery rate calculated successfully')
    } catch (error) {
      console.error('[VIPTable Menu] Error calculating delivery rate:', error)
    }
  }

  /**
   * Exibe mensagem sutil informando que o frete será informado pelo atendente (cidade diferente)
   * Não bloqueia o fluxo — o pedido segue normalmente.
   */
  _showDifferentCityNotice() {
    const deliveryRateContainer = document.getElementById('checkout-delivery-rate')
    if (!deliveryRateContainer) return

    // Mostrar a linha do frete com mensagem especial
    deliveryRateContainer.classList.remove('hidden')
    deliveryRateContainer.setAttribute('data-different-city', 'true')

    // Trocar o label esquerdo (Taxa de entrega:) para a mensagem sutil
    const labelEl = deliveryRateContainer.querySelector('.text-xs, .text-sm')
    if (labelEl) {
      labelEl.setAttribute('data-original-text', labelEl.textContent)
      labelEl.textContent = 'Parece que esse endereço é de uma cidade diferente de onde estamos. O valor exato da taxa de entrega será confirmado no WhatsApp.'
      labelEl.style.fontStyle = 'italic'
      labelEl.style.fontWeight = 'bold'
    }

    // Ocultar o valor (R$ X,XX) — a mensagem no label já é auto-explicativa
    if (this.hasDeliveryRateTarget) {
      this.deliveryRateTarget.setAttribute('data-original-text', this.deliveryRateTarget.textContent)
      this.deliveryRateTarget.textContent = ''
    }
  }

  _clearDifferentCityNotice() {
    const deliveryRateContainer = document.getElementById('checkout-delivery-rate')
    if (!deliveryRateContainer) return
    if (!deliveryRateContainer.getAttribute('data-different-city')) return

    deliveryRateContainer.removeAttribute('data-different-city')

    // Restaurar label original
    const labelEl = deliveryRateContainer.querySelector('.text-xs, .text-sm')
    if (labelEl) {
      const original = labelEl.getAttribute('data-original-text')
      if (original) labelEl.textContent = original
      labelEl.removeAttribute('data-original-text')
      labelEl.style.fontStyle = ''
      labelEl.style.fontWeight = ''
    }

    // Restaurar valor original
    if (this.hasDeliveryRateTarget) {
      const original = this.deliveryRateTarget.getAttribute('data-original-text')
      if (original) this.deliveryRateTarget.textContent = original
      this.deliveryRateTarget.removeAttribute('data-original-text')
    }

    // Esconder a linha (será reexibida por updateDeliveryRateUI se rate > 0)
    deliveryRateContainer.classList.add('hidden')
  }

  /**
   * Atualiza a UI com o valor da taxa de entrega
   */
  updateDeliveryRateUI(rate) {
    console.log('[VIPTable Menu] Updating delivery rate UI:', rate)
    
    // Atualizar a variável de instância
    this.deliveryRateValue = rate
    
    const deliveryRateContainer = document.getElementById('checkout-delivery-rate')
    
    // Se o modo different_city está ativo, não sobrescrever — _showDifferentCityNotice gerencia a UI
    if (deliveryRateContainer?.getAttribute('data-different-city')) {
      this.updateTotalPrice()
      this.validateSendButton()
      return
    }

    if (rate > 0) {
      // Mostrar linha de taxa de entrega
      if (deliveryRateContainer) {
        deliveryRateContainer.classList.remove('hidden')
      }
      
      // Atualizar valor
      if (this.hasDeliveryRateTarget) {
        this.deliveryRateTarget.textContent = this.formatCurrency(rate)
      }
    } else {
      // Ocultar linha se taxa for zero
      if (deliveryRateContainer) {
        deliveryRateContainer.classList.add('hidden')
      }
    }
    
    // IMPORTANTE: Recalcular o total após atualizar a taxa
    this.updateTotalPrice()
    
    // Validar se o botão de envio deve estar habilitado
    this.validateSendButton()
    
    console.log('[VIPTable Menu] Delivery rate UI updated, total recalculated')
  }

  /**
   * Helper: Pega o valor do select deliver_method que está VISÍVEL (não oculto)
   * Existem 2 selects com o mesmo name: #checkout-delivery-method-existing e #checkout-delivery-method-new
   * Apenas um deles está visível de cada vez, precisamos pegar o valor do visível
   */
  getVisibleDeliverMethod() {
    const selects = [
      document.getElementById('checkout-delivery-method-existing'),
      document.getElementById('checkout-delivery-method-new')
    ].filter(Boolean)
    
    // Retorna o valor do primeiro select que está visível (não oculto)
    for (const select of selects) {
      const isVisible = select.offsetParent !== null && !select.closest('.hidden')
      if (isVisible) {
        console.log('[VIPTable Menu] Found visible deliver_method select:', select.id, '=', select.value)
        return select.value
      }
    }
    
    // Fallback: retorna o primeiro select que encontrar (mesmo que oculto)
    const fallback = document.querySelector('select[name="order[deliver_method]"]')
    if (fallback) {
      console.warn('[VIPTable Menu] No visible deliver_method select found, using fallback:', fallback.value)
      return fallback.value
    }
    
    return null
  }

  // Normaliza aliases para o valor canônico do enum `Order#deliver_method` ("take_away" | "delivery")
  getNormalizedDeliverMethod() {
    const raw = (this.getVisibleDeliverMethod() || '').toString().toLowerCase()
    if (!raw) return null
    // aceitar variações sem alterar o valor canônico (defensivo)
    if (['takeaway', 'take_away'].includes(raw)) return 'take_away'
    if (['delivery'].includes(raw)) return 'delivery'
    return raw
  }

  /**
   * Helper: retorna o address field (por id) da seção de checkout VISÍVEL
   * (evita problemas com IDs duplicados quando o partial de endereço aparece
   *  em mais de uma cena). Se não encontrar em seções visíveis, faz fallback
   *  para document.getElementById.
   */
  getVisibleAddressField(fieldId) {
    const sections = [document.getElementById('checkout-existing'), document.getElementById('checkout-new')]
    for (const sec of sections) {
      if (!sec) continue
      if (sec.classList && sec.classList.contains('hidden')) continue
      const el = sec.querySelector(`#${fieldId}`)
      if (el) return el
    }
    return document.getElementById(fieldId)
  }

  /**
   * Valida se o botão "Enviar pelo WhatsApp" deve estar habilitado
   * Regras:
   * - Se método de entrega é "deliver", precisa ter endereço válido
   * - Endereço válido: UUID preenchido OU (CEP + número + identificação preenchidos)
   * - Atualiza mensagem progressiva guiando o usuário campo por campo
   */
  validateSendButton() {
    const sendBtn = document.getElementById('checkout-send-whatsapp')
    const validationMessage = document.getElementById('checkout-validation-message')
    if (!sendBtn) return

    // Lazy-wire: sempre que validateSendButton rodar, garante que o input de nome
    // do novo cliente já dispara revalidação ao ser alterado
    const nameInputNew = document.getElementById('checkout-name')
    if (nameInputNew && !nameInputNew._hasValidateHandler) {
      nameInputNew.addEventListener('input', () => this.validateSendButton())
      nameInputNew._hasValidateHandler = true
    }

    const disable = (msg) => {
      sendBtn.disabled = true
      sendBtn.classList.add('opacity-50', 'cursor-not-allowed')
      if (validationMessage) {
        validationMessage.textContent = msg
        validationMessage.classList.remove('hidden')
      }
    }

    const enable = () => {
      sendBtn.disabled = false
      sendBtn.classList.remove('opacity-50', 'cursor-not-allowed')
      if (validationMessage) validationMessage.classList.add('hidden')
    }

    // --- 1) Validar valor mínimo do pedido ---
    const minimumValue = window.VIPTableMenu?.minimumValue || 0
    if (minimumValue > 0) {
      const subtotal = this.cart.reduce((s, it) => s + (parseInt(it.price, 10) * parseInt(it.quantity, 10)), 0)
      if (subtotal < minimumValue) {
        disable(`⚠️ Pedido mínimo: ${this.formatCurrency(minimumValue)}`)
        return
      }
    }

    // --- 2) Validar troco (independe do método de entrega) ---
    const _newEl2 = document.getElementById('checkout-new')
    const _newDetails2 = document.getElementById('checkout-new-details')
    const _isNew = _newEl2 && !_newEl2.classList.contains('hidden')
                && _newDetails2 && !_newDetails2.classList.contains('hidden')
    const paymentSelectEl = _isNew
      ? document.getElementById('checkout-payment-method-new')
      : (document.getElementById('checkout-payment-method-existing') || document.querySelector('select[name="order[payment_method]"]'))
    if (paymentSelectEl?.value === 'money') {
      // hidden guarda reais inteiros (ex: 300 = R$ 300,00); converter para centavos para comparar
      const changeForCents = parseInt(document.getElementById('checkout-change-for-cents')?.value || '0', 10) * 100
      const subtotal = this.cart.reduce((s, it) => s + (parseInt(it.price, 10) * parseInt(it.quantity, 10)), 0)
      const rate = parseInt(document.getElementById('delivery_rate_field')?.value || '0', 10)
      const total = subtotal + rate
      if (changeForCents > 0 && changeForCents < total) {
        disable(`⚠️ Informe o troco para um valor acima de ${this.formatCurrency(total)}`)
        return
      }
    }

    // --- 2) Validar nome do novo cliente ---
    const newEl = document.getElementById('checkout-new')
    const newDetailsEl = document.getElementById('checkout-new-details')
    const isNewClientFlow = newEl && !newEl.classList.contains('hidden')
                         && newDetailsEl && !newDetailsEl.classList.contains('hidden')
    if (isNewClientFlow) {
      const nameVal = document.getElementById('checkout-name')?.value?.trim()
      if (!nameVal) {
        disable('⚠️ Informe seu nome para continuar')
        return
      }
    }

    // --- 3) Validar endereço (apenas delivery) ---
    const deliverMethod = this.getNormalizedDeliverMethod()

    if (!deliverMethod || deliverMethod === 'take_away') {
      enable()
      return
    }

    const addressUuidInput = document.getElementById('order_clientship_address_id')
    if (addressUuidInput && addressUuidInput.value && addressUuidInput.value.trim() !== '') {
      enable()
      return
    }

    const zipcodeField = this.getVisibleAddressField('order_clientship_address_zipcode')
    const streetNumberField = this.getVisibleAddressField('order_clientship_address_street_number')
    const nameField = this.getVisibleAddressField('order_clientship_address_name')
      const neighborhoodField = this.getVisibleAddressField('order_clientship_address_neighborhood')
      const streetField = this.getVisibleAddressField('order_clientship_address_street')

    const hasValidZipcode = zipcodeField && zipcodeField.value.replace(/\D/g, '').length === 8
    const hasValidNumber = streetNumberField && streetNumberField.value.trim() !== ''
      // Se campo está editável, é obrigatório
      const neighborhoodRequired = neighborhoodField && !neighborhoodField.hasAttribute('readonly')
      const streetRequired = streetField && !streetField.hasAttribute('readonly')
      const hasValidNeighborhood = neighborhoodField && (neighborhoodRequired ? neighborhoodField.value.trim() !== '' : true)
      const hasValidStreet = streetField && (streetRequired ? streetField.value.trim() !== '' : true)

      if (hasValidZipcode && hasValidNumber && hasValidNeighborhood && hasValidStreet) {
      enable()
    } else {
      let message = ''
      if (!hasValidZipcode) message = '⚠️ Informe o CEP para continuar'
      else if (!hasValidNumber) message = '⚠️ Informe o número para continuar'
        else if (neighborhoodRequired && !hasValidNeighborhood) message = '⚠️ Informe o bairro para continuar'
        else if (streetRequired && !hasValidStreet) message = '⚠️ Informe a rua para continuar'
      disable(message)
    }
  }

  // ==========================================

  // ==========================================
  // CART DRAWER
  // ==========================================

  /**
   * Abre o drawer do carrinho
   */
  openCart() {
    if (!this.hasCartDrawerTarget) return

    this.cartDrawerTarget.classList.remove('pointer-events-none')
    
    if (this.hasCartOverlayTarget) {
      this.cartOverlayTarget.classList.remove('opacity-0', 'pointer-events-none')
      this.cartOverlayTarget.classList.add('opacity-100', 'pointer-events-auto')
    }
    
    if (this.hasCartPanelTarget) {
      this.cartPanelTarget.classList.remove('translate-x-full')
      this.cartPanelTarget.classList.add('translate-x-0')
    }

    // Prevenir scroll do body
    document.body.classList.add('no-scroll')
  }

  /**
   * Fecha o drawer do carrinho
   */
  closeCart() {
    if (!this.hasCartDrawerTarget) return

    if (this.hasCartOverlayTarget) {
      this.cartOverlayTarget.classList.add('opacity-0', 'pointer-events-none')
      this.cartOverlayTarget.classList.remove('opacity-100', 'pointer-events-auto')
    }
    
    if (this.hasCartPanelTarget) {
      this.cartPanelTarget.classList.add('translate-x-full')
      this.cartPanelTarget.classList.remove('translate-x-0')
    }

    // Restaurar scroll do body após animação
    setTimeout(() => {
      this.cartDrawerTarget.classList.add('pointer-events-none')
      document.body.classList.remove('no-scroll')
    }, 500)
  }

  // ==========================================
  // ORDER MANAGEMENT
  // ==========================================

  /**
   * Envia pedido via WhatsApp
   */
  sendOrder() {
    // New UX: navigate to full‑page v2 checkout instead of opening an in‑page modal.
    if (this.cart.length === 0) {
      this.showToast('Adicione itens ao carrinho primeiro', 'warning')
      return
    }

    // Verificar valor mínimo antes de prosseguir para o checkout
    const minimumValue = window.VIPTableMenu?.minimumValue || 0
    if (minimumValue > 0) {
      const subtotal = this.cart.reduce((s, it) => s + (parseInt(it.price, 10) * parseInt(it.quantity, 10)), 0)
      if (subtotal < minimumValue) {
        this.showToast(`Pedido mínimo: ${this.formatCurrency(minimumValue)}. Adicione mais itens para continuar.`, 'warning')
        return
      }
    }

    const slug = this.companySlugValue || window.VIPTableMenu?.companySlug
    if (slug) {
      let url = `/companies/${slug}/checkout`
      // include club_code query param — prefer Stimulus value, then globals
      const clubCode = this.clubCodeValue || window.VIPTableMenu?.club_code || window.CONFIG?.club_code
      if (clubCode) {
        url += `?club_code=${encodeURIComponent(clubCode)}`
      }
      window.location.href = url;
      return;
    }

    // fallback: slug não resolvido — erro crítico de configuração
    console.error('[VIPTable Menu] companySlug não encontrado — impossível prosseguir com checkout')
    this.showToast('Erro interno: empresa não identificada. Recarregue a página.', 'error')
  }
  async persistAndSend(event) {
    event?.preventDefault();

    const trigger = event?.currentTarget || document.getElementById('btn-send-simple-order');
    const originalTriggerHTML = trigger?.innerHTML;
    if (trigger) {
      trigger.disabled = true;
      trigger.innerHTML = `<svg class="animate-spin w-5 h-5 mr-2 inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.568 3 7.291l3-2.709z"></path></svg> Enviando...`;
    }

    // Overlay de loading cobrindo toda a tela
    this._showOrderLoadingOverlay()

    let orderSucceeded = false;

    try {
      if (!this.cart || this.cart.length === 0) {
        this.showToast('Carrinho vazio — adicione itens antes de enviar', 'warning')
        return
      }

      const form = document.querySelector('#new_order')

      // Sem formulário #new_order não é possível persistir o pedido no banco.
      // Bloquear o envio para garantir que o WhatsApp nunca abra sem persistência.
      if (!form) {
        console.error('[VIPTable Menu] Formulário #new_order não encontrado — impossível persistir pedido')
        this.showToast('Erro interno: formulário de pedido não encontrado. Recarregue a página.', 'error')
        return
      }

      // 1) populate legacy-shaped `original_json_order` from v2 cart
      // Formato esperado por Order#parse_original_json_order_into_object:
      //   [ [{food_informations, price, optionals, additionals, flavours, ...}], [{...}], ... ]
      // food_from_json acessa json_item[0], portanto cada item é um array de 1 elemento.
      const legacyOrders = this.cart.map(item => {
        // price.price deve ser o preço BASE (sem adicionais), pois OrdersFood#total_price
        // adiciona os adicionais em cima dele.
        // Itens com price_data (código atual): usa price_data.price diretamente.
        // Itens sem price_data (localStorage antigo): deriva o base subtraindo adicionais
        // de item.price para evitar double-count em produção.
        let basePrice
        if (item.price_data?.price != null) {
          basePrice = parseInt(item.price_data.price, 10)
        } else {
          const additionalsSum = (item.additionals || []).reduce((s, a) => s + parseInt(a.price || 0, 10), 0)
          basePrice = parseInt(item.price, 10) - additionalsSum
          if (additionalsSum > 0) {
            console.warn('[VIPTable] Legacy cart item detected (no price_data) — derived basePrice:', {
              itemName: item.name, itemPrice: item.price, additionalsSum, basePrice
            })
          }
        }
        return [{
        food_informations: { id: parseInt(item.id, 10), name: item.name },
        price: {
          price: basePrice,
          text: item.price_data?.text || ''
        },
        pizza: item.pizza === 'true' || item.pizza === true,
        observations: item.observations || '',
        optionals: item.optionals || [],
        additionals: item.additionals || [],
        flavours: item.flavours || [],
        quantity: item.quantity,
        final_price: parseInt(item.price, 10) * item.quantity
      }]})

      const originalJsonInput = form.querySelector('#original_json_order') || document.getElementById('original_json_order')
      if (originalJsonInput) originalJsonInput.value = JSON.stringify(legacyOrders)

      // 2) prefer checkout inputs (scene) if present — keep server field names in sync
      const scenePhone = document.getElementById('checkout-phone')
      const sceneName = document.getElementById('checkout-name')
      const phoneField = form.querySelector('input[name="order[clientship_attributes][phone]"]') || document.getElementById('order_clientship_attributes_phone')
      const nameField = form.querySelector('input[name="order[clientship_attributes][name]"]') || document.getElementById('order_clientship_attributes_name')
      if (scenePhone && phoneField) phoneField.value = scenePhone.value
      // Only overwrite nameField if sceneName has a value — avoids blanking the
      // hidden field that was already populated by the clientship lookup response.
      if (sceneName && sceneName.value && nameField) nameField.value = sceneName.value
      
      // Desabilitar o select deliver_method que está oculto para evitar
      // que o FormData envie dois valores para order[deliver_method]
      const deliverMethodSelects = [
        document.getElementById('checkout-delivery-method-existing'),
        document.getElementById('checkout-delivery-method-new')
      ].filter(Boolean)

      deliverMethodSelects.forEach(select => {
        const isVisible = select.offsetParent !== null && !select.closest('.hidden')
        if (!isVisible) {
          select.disabled = true
        } else {
          console.log('[VIPTable Menu] Using visible deliver_method select:', select.id, '=', select.value)
        }
      })

      // Se TODOS os selects foram desabilitados (ex: novo cliente ainda na tela de telefone,
      // ou cliente existente cujo #checkout-existing ainda está com .hidden), injetar um
      // campo hidden para garantir que deliver_method seja enviado. Sem isso, Rails defaulta
      // para enum index 0 (delivery) mesmo que o pedido seja take_away.
      const anyDeliverEnabled = deliverMethodSelects.some(s => !s.disabled)
      if (!anyDeliverEnabled && deliverMethodSelects.length > 0) {
        const fallbackVal = deliverMethodSelects[0]?.value || 'take_away'
        const hiddenDeliver = document.createElement('input')
        hiddenDeliver.type = 'hidden'
        hiddenDeliver.name = 'order[deliver_method]'
        hiddenDeliver.value = fallbackVal
        hiddenDeliver.id = '_deliver_method_fallback'
        form.appendChild(hiddenDeliver)
        console.log('[VIPTable Menu] All deliver_method selects disabled — injecting fallback hidden field:', fallbackVal)
      }

      // Desabilitar o select payment_method oculto (mesmo padrão do deliver_method)
      const paymentMethodSelects = [
        document.getElementById('checkout-payment-method-existing'),
        document.getElementById('checkout-payment-method-new')
      ].filter(Boolean)

      let activePaymentSelect = null
      paymentMethodSelects.forEach(select => {
        const isVisible = select.offsetParent !== null && !select.closest('.hidden')
        if (!isVisible) {
          select.disabled = true
        } else {
          activePaymentSelect = select
        }
      })

      // Desabilitar o input de cupom inativo — mesmo padrão do deliver_method.
      // FormData inclui AMBOS os inputs (CSS .hidden não desabilita o campo),
      // então o Rails recebia o valor do input inativo (vazio) sobrescrevendo o ativo.
      const couponInputs = [
        document.getElementById('checkout-coupon-code-existing'),
        document.getElementById('checkout-coupon-code-new')
      ].filter(Boolean)

      couponInputs.forEach(input => {
        const isVisible = input.offsetParent !== null && !input.closest('.hidden')
        if (!isVisible) input.disabled = true
      })

      // 3) Copy visible checkout address fields into the canonical hidden inputs
      //    in #new_order. Those hidden inputs are the ONLY ones with name attrs
      //    for address fields — the partial inputs have no `name`, eliminating
      //    duplicate-field issues in FormData.
      const copyVisibleToHidden = (visibleId, hiddenId) => {
        const vis = this.getVisibleAddressField(visibleId)
        const tgt = document.getElementById(hiddenId)
        if (vis && tgt) {
          // Strip mask (keep only digits) for zipcode
          if (hiddenId === 'form_address_zipcode') {
            tgt.value = (vis.value || '').replace(/\D/g, '')
          } else {
            tgt.value = vis.value || ''
          }
        }
      }

      copyVisibleToHidden('order_clientship_address_zipcode',         'form_address_zipcode')
      copyVisibleToHidden('order_clientship_address_street',          'form_address_street')
      copyVisibleToHidden('order_clientship_address_street_number',   'form_address_street_number')
      copyVisibleToHidden('order_clientship_address_address_complement', 'form_address_address_complement')
      copyVisibleToHidden('order_clientship_address_neighborhood',    'form_address_neighborhood')
      copyVisibleToHidden('order_clientship_address_city_name',       'form_address_city_name')
      copyVisibleToHidden('order_clientship_address_state_abbreviation', 'form_address_state_abbreviation')
      copyVisibleToHidden('order_clientship_address_name',            'form_address_name')

      // Copiar lat/lon dos hidden fields do partial (não têm ID único alternativo)
      const latVis  = this.getVisibleAddressField('order_clientship_address_latitude')  || document.querySelector('.latitude')
      const lonVis  = this.getVisibleAddressField('order_clientship_address_longitude') || document.querySelector('.longitude')
      const latTgt  = document.getElementById('form_address_latitude')
      const lonTgt  = document.getElementById('form_address_longitude')
      if (latVis && latTgt) latTgt.value = latVis.value || ''
      if (lonVis && lonTgt) lonTgt.value = lonVis.value || ''

      console.log('[VIPTable Menu] Address hidden inputs after copy:', {
        zipcode: document.getElementById('form_address_zipcode')?.value,
        street:  document.getElementById('form_address_street')?.value,
        city:    document.getElementById('form_address_city_name')?.value,
      })

      // 4) Validate change_for: must be >= total when payment is money
      if (activePaymentSelect?.value === 'money') {
        // hidden guarda reais inteiros; converter para centavos para comparar
        const changeForCents = parseInt(document.getElementById('checkout-change-for-cents')?.value || '0', 10) * 100
        const subtotalForValidation = this.cart.reduce((s, it) => s + (parseInt(it.price, 10) * parseInt(it.quantity, 10)), 0)
        const deliveryRateForValidation = parseInt(document.getElementById('delivery_rate_field')?.value || '0', 10)
        const totalForValidation = subtotalForValidation + deliveryRateForValidation
        if (changeForCents > 0 && changeForCents < totalForValidation) {
          const isNew = activePaymentSelect.id === 'checkout-payment-method-new'
          const errorEl = document.getElementById(isNew ? 'checkout-change-for-error-new' : 'checkout-change-for-error')
          if (errorEl) {
            errorEl.textContent = `Informe o troco para um valor acima de ${this.formatCurrency(totalForValidation)}`
            errorEl.classList.remove('hidden')
          }
          const changeInput = document.getElementById(isNew ? 'checkout-change-for-new' : 'checkout-change-for-existing')
          changeInput?.focus()
          if (trigger) trigger.classList.remove('disabled')
          return
        }
      }

      const formData = new FormData(form)
      // DEBUG: inspecionar exatamente o que será enviado ao servidor (ajuda a detectar campos vazios/duplicados)
      console.log('[VIPTable Menu] FormData entries before submit:', Array.from(formData.entries()))
      const csrf = document.querySelector('meta[name="csrf-token"]')?.content
      const body = new URLSearchParams(Array.from(formData.entries()))

      const resp = await fetch(form.getAttribute('action'), {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 
          'Accept': 'application/json',
          'Content-Type': 'application/x-www-form-urlencoded',
          ...(csrf ? { 'X-CSRF-Token': csrf } : {}) 
        },
        body
      })

      if (resp.status === 409) {
        const conflict = await resp.json().catch(() => ({}))
        if (conflict.code === 'prices_changed') {
          // Nothing was persisted: fix the cart in place and let the customer resend
          document.getElementById('_deliver_method_fallback')?.remove()
          this._removeOrderLoadingOverlay()
          this.applyServerPriceCorrections(conflict)
          return
        }
      }

      if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({}))
        const errorMessage = errorData.message || `Falha ao persistir pedido (${resp.status})`
        this.showToast(errorMessage, 'error')
        throw new Error(errorMessage)
      }
      
      // Parse resposta JSON
      const orderData = await resp.json()
      if (!orderData.success) {
        this.showToast(orderData.message || 'Erro ao criar pedido', 'error')
        throw new Error(orderData.message || 'Order creation failed')
      }
      
      console.log('[VIPTable Menu] Order persisted successfully:', orderData)
      
      // Re-habilitar os selects deliver_method que foram desabilitados
      deliverMethodSelects.forEach(select => {
        select.disabled = false
      })
      // Remover campo fallback de deliver_method caso tenha sido injetado
      document.getElementById('_deliver_method_fallback')?.remove()

      // 4) build WhatsApp message
      const restaurantWhatsApp = this.whatsappValue || window.VIPTableMenu?.whatsappNumber || localStorage.getItem('restaurant_whatsapp')
      const SEP = '\n'
      let message = 'Olá, gostaria de fazer o seguinte pedido:'

      // ── ITENS DO PEDIDO ──
      message += `${SEP}\n\u{1F6D2} *ITENS DO PEDIDO*${SEP}`
      this.cart.forEach(item => {
        const itemTotal  = this.formatCurrency(parseInt(item.price, 10) * parseInt(item.quantity, 10))
        const variation  = item.price_data?.text ? ` _(${item.price_data.text})_` : ''
        message += `\n\n*${item.quantity}x ${item.name}*${variation} — ${itemTotal}`

        if (item.flavours && item.flavours.length > 0) {
          message += `\n  \u{1F355} Sabores:`
          item.flavours.forEach(f => {
            const label = f.name || f.text || ''
            const price = parseInt(f.price, 10)
            message += `\n    • ${label}${price > 0 ? ' — ' + this.formatCurrency(price) : ''}`
          })
        }

        if (item.additionals && item.additionals.length > 0) {
          message += `\n Adicionais:`
          item.additionals.forEach(a => {
            const label = a.name || a.text || ''
            const price = parseInt(a.price, 10)
            message += `\n    • ${label}${price > 0 ? ' — ' + this.formatCurrency(price) : ''}`
          })
        }

        if (item.optionals && item.optionals.length > 0) {
          message += `\n Opcionais:`
          item.optionals.forEach(o => { message += `\n    • ${o.name || o.text || ''}` })
        }

        if (item.observations && item.observations.trim()) {
          message += `\n Observações:\n    _${item.observations.trim()}_`
        }
      })

      // ── DADOS DO CLIENTE ──
      const clientName  = (nameField  && nameField.value)  || ''
      const clientPhone = (phoneField && phoneField.value) || ''
      if (clientName || clientPhone) {
        message += `\n${SEP}\n\u{1F464} *DADOS DO CLIENTE*${SEP}`
        if (clientName)  message += `\n Nome: ${clientName}`
        if (clientPhone) message += `\n Telefone: ${clientPhone}`
      }

      // ── ENTREGA ──
      const deliverMethod     = this.getNormalizedDeliverMethod()
      const deliveryRateField = document.getElementById('delivery_rate_field')
      const deliveryRateCents = deliveryRateField ? parseInt(deliveryRateField.value || 0, 10) : 0

      if (deliverMethod === 'delivery') {
        let streetLine     = ''
        let complementLine = ''
        let cepLine        = ''

        // Prefer server-persisted address data (ensures consistency with print/admin views)
        const serverAddr = orderData.order?.address
        if (serverAddr && serverAddr.street) {
          const street       = serverAddr.street || ''
          const number       = serverAddr.street_number || ''
          const neighborhood = serverAddr.neighborhood || ''
          const city         = serverAddr.city_name || ''
          const state        = serverAddr.state_abbreviation || ''
          const zipcode      = serverAddr.zipcode || ''
          const complement   = serverAddr.address_complement || ''
          streetLine = `${street}${number ? ', ' + number : ''}${neighborhood ? ' — ' + neighborhood : ''}${city ? ', ' + city : ''}${state ? ' — ' + state : ''}`
          if (complement) complementLine = complement
          if (zipcode)    cepLine = `CEP: ${zipcode}`
        } else {
          // Fallback: read from UI (address card data-* or form fields)
          const addrUuid = document.getElementById('order_clientship_address_id')?.value
          if (addrUuid) {
            const activeCard = document.querySelector(`.address-card[data-uuid="${addrUuid}"]`)
                            || document.querySelector('.address-card.active')
            if (activeCard) {
              const street       = activeCard.getAttribute('data-street') || ''
              const number       = activeCard.getAttribute('data-street-number') || ''
              const neighborhood = activeCard.getAttribute('data-neighborhood') || ''
              const city         = activeCard.getAttribute('data-city-name') || ''
              const state        = activeCard.getAttribute('data-state-abbreviation') || ''
              const zipcode      = activeCard.getAttribute('data-zipcode') || ''
              const complement   = activeCard.getAttribute('data-complement') || ''
              streetLine = `${street}${number ? ', ' + number : ''}${neighborhood ? ' — ' + neighborhood : ''}${city ? ', ' + city : ''}${state ? ' — ' + state : ''}`
              if (complement) complementLine = complement
              if (zipcode)    cepLine = `CEP: ${zipcode}`
            }
          } else {
            const street       = this.getVisibleAddressField('order_clientship_address_street')?.value || ''
            const number       = this.getVisibleAddressField('order_clientship_address_street_number')?.value || ''
            const complement   = this.getVisibleAddressField('order_clientship_address_address_complement')?.value || ''
            const neighborhood = this.getVisibleAddressField('order_clientship_address_neighborhood')?.value || ''
            const city         = this.getVisibleAddressField('order_clientship_address_city_name')?.value || ''
            const state        = this.getVisibleAddressField('order_clientship_address_state_abbreviation')?.value || ''
            const zipcode      = this.getVisibleAddressField('order_clientship_address_zipcode')?.value || ''
            streetLine = `${street}${number ? ', ' + number : ''}${neighborhood ? ' — ' + neighborhood : ''}${city ? ', ' + city : ''}${state ? ' — ' + state : ''}`
            if (complement) complementLine = complement
            if (zipcode)    cepLine = `CEP: ${zipcode}`
          }
        }

        message += `\n${SEP}\n\u{1F697} *ENTREGA*${SEP}`
        if (streetLine)     message += `\n ${streetLine}`
        if (complementLine) message += `\n ${complementLine}`
        if (cepLine)        message += `\n ${cepLine}`
      } else {
        message += `\n${SEP}\n\u{1F3EA} *RETIRADA NO BALC\u00C3O*${SEP}`
      }

      // ── PAGAMENTO ──
      const paymentSelect     = document.querySelector('select[name="order[payment_method]"]') || document.getElementById('order_payment_method')
      const paymentText       = paymentSelect?.options ? paymentSelect.options[paymentSelect.selectedIndex].text : (paymentSelect?.value || '')
      const changeForCentsVal = parseInt(document.getElementById('checkout-change-for-cents')?.value || '0', 10) * 100

      if (paymentText || changeForCentsVal > 0) {
        message += `\n${SEP}\n\u{1F4B3} *PAGAMENTO*${SEP}`
        if (paymentText)            message += `\n Forma: ${paymentText}`
        if (changeForCentsVal > 0)  message += `\n Troco para: ${this.formatCurrency(changeForCentsVal)}`
      }

      // ── RESUMO FINANCEIRO ──
      const subtotalCents = this.cart.reduce((s, it) => s + (parseInt(it.price, 10) * parseInt(it.quantity, 10)), 0)
      const totalCents    = subtotalCents + (deliverMethod === 'delivery' ? deliveryRateCents : 0)

      message += `\n${SEP}\n *RESUMO FINANCEIRO*${SEP}`
      message += `\n Subtotal: ${this.formatCurrency(subtotalCents)}`
      if (deliverMethod === 'delivery') {
        const calcMethod = document.getElementById('delivery_method_calc')?.value || ''
        if (calcMethod === 'different_city') {
          message += `\n Taxa de entrega: *A INFORMAR*`
          message += `\n\n⚠️ *ATENÇÃO ATENDENTE: o endereço de entrega é de uma cidade diferente da cidade do estabelecimento. O valor do frete não foi calculado automaticamente e deve ser informado ao cliente.*`
        } else if (deliveryRateCents > 0) {
          message += `\n Taxa de entrega: ${this.formatCurrency(deliveryRateCents)}`
        }
      }

      if (orderData.coupon) {
        message += `\n TOTAL: ${this.formatCurrency(totalCents)}`
        message += `\n Cupom: ${orderData.coupon.code}`
        message += `\n Desconto _(condicionado a validação pelo atendente)_: -${this.formatCurrency(orderData.coupon.discount)}`
        message += `\n *TOTAL A PAGAR _(condicionado a validação pelo atendente)_: ${this.formatCurrency(orderData.coupon.total_to_pay)}*`
      } else {
        message += `\n *TOTAL: ${this.formatCurrency(totalCents)}*`
      }

      // ── LINK INTERNO (visível só para o estabelecimento) ──
      if (orderData.check_link) {
        message += `\n\n\u{1F517} _Painel do estabelecimento:_\n_${orderData.check_link}_`
      }

      // Salvar dados do WhatsApp no localStorage.
      // A página de sucesso fará o redirect automaticamente.
      if (restaurantWhatsApp) {
        localStorage.setItem('viptable_pending_whatsapp', JSON.stringify({ phone: this._waPhone(restaurantWhatsApp), message }))
      }

      // Limpar carrinho antes de redirecionar
      this.cart = []
      this.saveCart()
      this.updateCartUI()
      window.dispatchEvent(new CustomEvent('viptable:cart:cleared', { detail: { company: this.companySlugValue } }))

      // Redirecionar a aba atual para a página de sucesso
      orderSucceeded = true
      const successUrl = `/v2/companies/${this.companySlugValue}/orders/${orderData.order.uuid}/success`
      window.location.href = successUrl

    } catch (err) {
      console.error('[VIPTable Menu] persistAndSend error', err)
      this._removeOrderLoadingOverlay()
      this.showToast('Erro ao enviar pedido. Tente novamente.', 'error')
    } finally {
      // Re-habilitar campos desabilitados e restaurar atributos removidos
      if (typeof deliverMethodSelects !== 'undefined') {
        deliverMethodSelects.forEach(select => { select.disabled = false })
      }
      if (typeof paymentMethodSelects !== 'undefined') {
        paymentMethodSelects.forEach(select => { select.disabled = false })
      }
      if (typeof couponInputs !== 'undefined') {
        couponInputs.forEach(input => { input.disabled = false })
      }
      
      if (typeof hiddenFieldNames !== 'undefined' && hiddenFieldNames.size > 0) {
        hiddenFieldNames.forEach((originalName, field) => {
          field.setAttribute('name', originalName)
        })
      }

      // Restaurar o botão de envio apenas se o pedido falhou.
      // Em caso de sucesso, a página já está sendo redirecionada.
      if (!orderSucceeded && trigger) {
        trigger.disabled = false
        if (originalTriggerHTML) trigger.innerHTML = originalTriggerHTML
        trigger.classList.remove('disabled')
      }
    }
  }

  // ==========================================
  // CHECKOUT (phone collection modal)
  // ==========================================

  showCheckoutModal() {
    // remove existing modal if present
    const existing = document.querySelector('#checkout-modal')
    if (existing) existing.remove()

    const modal = document.createElement('div')
    modal.id = 'checkout-modal'
    modal.className = 'fixed inset-0 z-[110] flex items-end sm:items-center justify-center'
    modal.innerHTML = `
      <div class="modal-backdrop fixed inset-0 bg-black bg-opacity-50 transition-opacity"></div>
      <div class="modal-content relative rounded-t-3xl sm:rounded-2xl w-full sm:max-w-md max-h-[90vh] overflow-hidden transform transition-all p-6 bg-white text-black shadow-2xl">
        <button class="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center" data-action="click->companies--v2--menu#closeCheckoutModal">
          <svg class="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
        </button>

        <div class="space-y-4">
          <h2 class="text-2xl font-bold">Ótimas escolhas!</h2>
          <h4 class="text-sm text-gray-600">Só precisamos de mais algumas informações para finalizar</h4>

          <form id="checkout-phone-form" class="mt-4">
            <label for="checkout-phone" class="block text-sm font-medium mb-2">Qual é seu telefone caso a gente precise te contactar?</label>
            <div>
              <input id="checkout-phone" name="phone" type="tel" inputmode="numeric" class="w-full p-3 border rounded-lg text-base" placeholder="(99) 99999-9999" autofocus />
              <p id="checkout-phone-error" class="mt-2 text-sm text-red-500 hidden">Número inválido — precisa ser um celular (começando com 9).</p>
            </div>
          </form>

          <div class="flex justify-end gap-3 mt-2">
            <button id="checkout-continue" class="px-4 py-2 rounded-lg theme-btn-accent text-white disabled:opacity-50" disabled aria-label="Enviar pelo WhatsApp">Enviar pelo WhatsApp</button>
          </div>
        </div>
      </div>
    `

    document.body.appendChild(modal)
    document.body.classList.add('no-scroll')

    // hook events
    this.setupCheckoutModalEventListeners()
  }

  closeCheckoutModal(event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
    const modal = document.querySelector('#checkout-modal')
    if (modal) modal.remove()
    document.body.classList.remove('no-scroll')
  }

  /**
   * Show Scene 2 (client exists) on the standalone checkout page.
   * - shows `#checkout-existing`, hides `#checkout-new`
   * - fills greeting and wires payment->change_for visibility
   */
  showCheckoutClientExists(json = {}) {
    const existingEl = document.getElementById('checkout-existing')
    const newEl = document.getElementById('checkout-new')
    if (!existingEl) return

    if (newEl) newEl.classList.add('hidden')
    existingEl.classList.remove('hidden')

    // hide the Scene‑1 intro (title + subtitle) when showing Scene‑2
    const introEl = document.getElementById('checkout-intro')
    if (introEl) introEl.classList.add('hidden')

    const nameSpan = document.getElementById('checkout-existing-name')
    if (nameSpan) nameSpan.textContent = json.name || ''

    // populate order fields if present (keep naming consistent with orders/_new.html.slim)
    const phoneField = document.querySelector('input[name="order[clientship_attributes][phone]"]') || document.getElementById('order_clientship_attributes_phone')
    if (phoneField && json.phone) phoneField.value = json.phone
    const nameField = document.getElementById('order_clientship_attributes_name') || document.querySelector('input[name="order[clientship_attributes][name]"]')
    if (nameField && json.name) nameField.value = json.name

    // Ensure delivery select carries the expected name (already in markup)
    const deliverySelect = document.getElementById('checkout-delivery-method-existing')
    if (deliverySelect && !deliverySelect.name) deliverySelect.name = 'order[deliver_method]'

    // Payment select: remove online option (defensive) and wire change_for visibility
    const paymentSelect = document.getElementById('checkout-payment-method-existing') || document.querySelector('select[name="order[payment_method]"]')
    if (paymentSelect) {
      const onlineOpt = paymentSelect.querySelector('option[value="online_payment"]')
      if (onlineOpt) onlineOpt.remove()

      if (!paymentSelect._hasChangeHandler) {
        paymentSelect.addEventListener('change', (ev) => {
          const changeContainer = document.getElementById('checkout-change-for-container')
          if (!changeContainer) return
          if (ev.target.value === 'money') {
            changeContainer.classList.remove('hidden')
            this.setupChangeForMask()
          } else {
            changeContainer.classList.add('hidden')
          }
          this.validateSendButton()
        })
        paymentSelect._hasChangeHandler = true
      }

      // set initial visibility for change_for
      const changeContainer = document.getElementById('checkout-change-for-container')
      if (changeContainer) {
        if (paymentSelect.value === 'money') {
          changeContainer.classList.remove('hidden')
          this.setupChangeForMask()
        } else {
          changeContainer.classList.add('hidden')
        }
      }
    }

    // Addresses handling: render cards if server returned addresses; wire delivery select
    const addresses = json.addresses || []
    const addressesSection = document.getElementById('checkout-addresses-section')
    const addressesListEl = addressesSection ? addressesSection.querySelector('.checkout-addresses-list') : null
    const noAddressEl = document.querySelector('.checkout-no-address')

    // Delay showing the 'Enviar pelo WhatsApp' button so the user has time to read options
    const sendBtn = document.getElementById('checkout-send-whatsapp')
    if (sendBtn) {
      sendBtn.disabled = false
      sendBtn.classList.add('hidden')
      clearTimeout(sendBtn._delayedShowTimeout)
      sendBtn._delayedShowTimeout = setTimeout(() => {
        sendBtn.classList.remove('hidden')
        sendBtn._delayedShowTimeout = null
      }, 2000)
    }

    const renderAddresses = (list) => {
      if (!addressesListEl) return
      addressesListEl.innerHTML = ''
      list.forEach((a, idx) => {
        const card = document.createElement('button')
        card.type = 'button'
        card.className = 'address-card p-3 rounded border text-left bg-transparent'
        card.setAttribute('data-uuid', a.uuid)
        card.setAttribute('data-zipcode', a.zipcode || '')
        card.setAttribute('data-street', a.street || '')
        card.setAttribute('data-street-number', a.street_number || '')
        card.setAttribute('data-neighborhood', a.neighborhood || '')
        card.setAttribute('data-city-name', a.city_name || '')
        card.setAttribute('data-state-abbreviation', a.state_abbreviation || '')
        card.setAttribute('data-complement', a.address_complement || '')
        card.setAttribute('data-name', a.name || '')

        if (idx === 0) card.classList.add('active')

        const title = document.createElement('div')
        title.className = 'font-medium'
        title.textContent = a.name || a.street || 'Endereço'

        const subtitle = document.createElement('div')
        subtitle.className = 'text-sm text-gray-400'
        subtitle.textContent = `${a.street || ''}${a.street_number ? ', '+a.street_number : ''}${a.neighborhood ? ' - '+a.neighborhood : ''}`

        card.appendChild(title)
        card.appendChild(subtitle)

        card.addEventListener('click', (ev) => {
          // Se o card já está ativo (selecionado), não refazer o cálculo — evita request duplicado
          if (card.classList.contains('active')) return

          // mark active
          addressesListEl.querySelectorAll('.address-card').forEach(n => n.classList.remove('active'))
          card.classList.add('active')

          // Apenas preencher o UUID - os campos do formulário são para NOVO endereço
          const addrUuidInput = document.getElementById('order_clientship_address_id')
          if (addrUuidInput) addrUuidInput.value = a.uuid
          
          // Calcular taxa de entrega para o endereço selecionado (usando apenas o UUID)
          this.calculateDeliveryRate()
          
          // Validar botão após selecionar endereço
          this.validateSendButton()
        })

        addressesListEl.appendChild(card)
      })

      // append 'Adicionar endereço' button (keeps same ID so existing CSS/handlers can reuse it)
      const addBtn = document.getElementById('checkout-add-address')
      if (addBtn) addBtn.classList.remove('hidden')

      // select first by default and populate hidden/order fields (defensive — don't rely only on click)
      const first = addressesListEl.querySelector('.address-card')
      if (first) {
        // visual active state
        addressesListEl.querySelectorAll('.address-card').forEach(n => n.classList.remove('active'))
        first.classList.add('active')

        // Apenas popular o UUID do primeiro endereço - campos do formulário são para NOVO endereço
        const a = list[0]
        const addrUuidInput = document.getElementById('order_clientship_address_id')
        if (addrUuidInput) addrUuidInput.value = a.uuid || ''
        
        // Calcular taxa de entrega para o primeiro endereço (usando apenas o UUID)
        this.calculateDeliveryRate()
        
        // Validar botão após selecionar primeiro endereço
        setTimeout(() => this.validateSendButton(), 200)
      }

      // Prevent page scroll when scrolling inside the addresses container
      if (!addressesListEl._hasScrollHandler) {
        addressesListEl.addEventListener('wheel', (e) => {
          const { scrollTop, scrollHeight, clientHeight } = addressesListEl
          const isAtTop = scrollTop === 0
          const isAtBottom = scrollTop + clientHeight >= scrollHeight
          
          // Prevent page scroll when scrolling inside container
          // Allow scroll to bubble only when at boundaries
          if ((e.deltaY < 0 && isAtTop) || (e.deltaY > 0 && isAtBottom)) {
            // At boundary, allow default behavior (page scroll)
            return
          }
          
          // Inside container, prevent page scroll
          e.preventDefault()
          e.stopPropagation()
        }, { passive: false })
        
        // Also handle touch events for mobile
        let touchStartY = 0
        addressesListEl.addEventListener('touchstart', (e) => {
          touchStartY = e.touches[0].clientY
        }, { passive: true })
        
        addressesListEl.addEventListener('touchmove', (e) => {
          const touchY = e.touches[0].clientY
          const deltaY = touchStartY - touchY
          const { scrollTop, scrollHeight, clientHeight } = addressesListEl
          const isAtTop = scrollTop === 0
          const isAtBottom = scrollTop + clientHeight >= scrollHeight
          
          if ((deltaY < 0 && isAtTop) || (deltaY > 0 && isAtBottom)) {
            return
          }
          
          e.stopPropagation()
        }, { passive: true })
        
        addressesListEl._hasScrollHandler = true
      }
    }

    // animated show / hide helpers for the addresses block
    const showAddressesSectionAnimated = () => {
      if (!addressesSection) return
      addressesSection.classList.remove('hidden')
      // Force reflow to enable transition
      addressesSection.offsetHeight
      // Use a fixed height that accommodates the addresses list (450px) + padding + labels + button
      addressesSection.style.maxHeight = '600px'
      addressesSection.style.opacity = '1'
    }

    const hideAddressesSectionAnimated = () => {
      if (!addressesSection) return
      addressesSection.style.maxHeight = '0'
      addressesSection.style.opacity = '0'
      // after transition, set hidden for accessibility
      setTimeout(() => {
        if (addressesSection.style.opacity === '0') addressesSection.classList.add('hidden')
      }, 300)
    }

    const showNoAddressAnimated = () => {
      if (!noAddressEl) return
      noAddressEl.classList.remove('hidden')
      // Force reflow to enable transition
      noAddressEl.offsetHeight
      noAddressEl.style.maxHeight = noAddressEl.scrollHeight + 'px'
      noAddressEl.style.opacity = '1'
    }

    const hideNoAddressAnimated = () => {
      if (!noAddressEl) return
      noAddressEl.style.maxHeight = '0'
      noAddressEl.style.opacity = '0'
      setTimeout(() => {
        if (noAddressEl.style.opacity === '0') noAddressEl.classList.add('hidden')
      }, 300)
    }

    const showAddressesUI = () => {
      if (addresses.length > 0) {
        showAddressesSectionAnimated()
        hideNoAddressAnimated()
        renderAddresses(addresses)
      } else {
        // no addresses — show full address form so user can enter a new address
        hideAddressesSectionAnimated()
        hideNoAddressAnimated()

        const newAddressForm = document.getElementById('checkout-new-address-form')
        if (newAddressForm) {
          newAddressForm.classList.remove('hidden')
          newAddressForm.offsetHeight
          newAddressForm.style.maxHeight = '800px'
          newAddressForm.style.opacity = '1'

          // Clear UUID so new address data is used
          const addressUuidInput = document.getElementById('order_clientship_address_id')
          if (addressUuidInput) addressUuidInput.value = ''

          // Clear all address fields
          const zipcodeField = this.getVisibleAddressField('order_clientship_address_zipcode')
          const cityField = this.getVisibleAddressField('order_clientship_address_city_name')
          const stateField = this.getVisibleAddressField('order_clientship_address_state_abbreviation')
          const neighborhoodField = this.getVisibleAddressField('order_clientship_address_neighborhood')
          const streetField = this.getVisibleAddressField('order_clientship_address_street')
          const streetNumberField = this.getVisibleAddressField('order_clientship_address_street_number')
          const complementField = this.getVisibleAddressField('order_clientship_address_address_complement')
          const nameField = this.getVisibleAddressField('order_clientship_address_name')

          if (zipcodeField) zipcodeField.value = ''
          if (cityField) cityField.value = ''
          if (stateField) stateField.value = ''
          if (neighborhoodField) { neighborhoodField.value = ''; neighborhoodField.setAttribute('readonly', 'readonly') }
          if (streetField) { streetField.value = ''; streetField.setAttribute('readonly', 'readonly') }
          if (streetNumberField) streetNumberField.value = ''
          if (complementField) complementField.value = ''
          if (nameField) nameField.value = ''

          // Hide additional fields until CEP is entered
          const additionalFields = this.getVisibleAddressField('address-additional-fields') || document.getElementById('address-additional-fields')
          if (additionalFields) {
            additionalFields.style.maxHeight = '0'
            additionalFields.style.opacity = '0'
            additionalFields.classList.add('hidden')
          }

          // Hide CEP error message if visible
          const errorMessage = document.getElementById('cep-error-message')
          if (errorMessage) errorMessage.style.display = 'none'

          // Esconder botão "Voltar aos endereços" — não há endereços para voltar
          const cancelBtn = newAddressForm.querySelector('#cancel-address-form')
          if (cancelBtn) cancelBtn.closest('.mt-6')?.classList.add('hidden')

          // Setup CEP mask and address field listeners
          this.setupCepField()
          this.setupAddressFieldListeners()

          setTimeout(() => {
            if (zipcodeField) zipcodeField.focus()
            this.validateSendButton()
          }, 350)
        }
      }
    }

    if (deliverySelect && !deliverySelect._hasChangeAddressesHandler) {
      deliverySelect.addEventListener('change', (ev) => {
        if (ev.target.value === 'delivery' || ev.target.value === 'deliver') {
          showAddressesUI()
        } else {
          // Retirada: zerar frete e atualizar total antes de esconder a seção
          this.updateDeliveryRateUI(0)

          // hide with animation
          hideAddressesSectionAnimated()
          hideNoAddressAnimated()
          
          // Esconder formulário de novo endereço se estiver visível
          const newAddressForm = document.getElementById('checkout-new-address-form')
          if (newAddressForm && !newAddressForm.classList.contains('hidden')) {
            newAddressForm.style.maxHeight = '0'
            newAddressForm.style.opacity = '0'
            setTimeout(() => {
              newAddressForm.classList.add('hidden')
            }, 300)
          }
        }
        
        // Validar botão ao mudar método de entrega
        this.validateSendButton()
      })
      deliverySelect._hasChangeAddressesHandler = true
    }

    // If client has addresses and delivery is already selected, show them immediately
    if (deliverySelect && (deliverySelect.value === 'deliver' || deliverySelect.value === 'delivery')) {
      showAddressesUI()
    }

    // wire Add address button to reveal address form when pressed
    const addAddrBtn = document.getElementById('checkout-add-address')
    const newAddressForm = document.getElementById('checkout-new-address-form')
    if (addAddrBtn && !addAddrBtn._hasHandler) {
      addAddrBtn.addEventListener('click', (ev) => {
        ev.preventDefault()
        
        // Esconder lista de endereços
        hideAddressesSectionAnimated()
        
        // Mostrar formulário de novo endereço
        if (newAddressForm) {
          newAddressForm.classList.remove('hidden')
          // Force reflow
          newAddressForm.offsetHeight
          newAddressForm.style.maxHeight = '800px'
          newAddressForm.style.opacity = '1'
          
          const zipcodeField = this.getVisibleAddressField('order_clientship_address_zipcode')
          const additionalFields = this.getVisibleAddressField('address-additional-fields') || document.getElementById('address-additional-fields')
          
          // IMPORTANTE: Limpar o UUID do endereço selecionado anteriormente
          // Isso força o calculateDeliveryRate a usar os dados do novo formulário
          const addressUuidInput = document.getElementById('order_clientship_address_id')
          if (addressUuidInput) {
            addressUuidInput.value = ''
            console.log('[VIPTable Menu] Cleared address UUID - will use new address data')
          }
          
          // SEMPRE limpar o formulário de endereço quando clicar em "Adicionar endereço"
          // Isso garante que o usuário comece do zero
          if (zipcodeField) {
            zipcodeField.value = ''
            console.log('[VIPTable Menu] Cleared zipcode field')
          }
          
          // Limpar todos os campos de endereço
          const cityField = this.getVisibleAddressField('order_clientship_address_city_name')
          const stateField = this.getVisibleAddressField('order_clientship_address_state_abbreviation')
          const neighborhoodField = this.getVisibleAddressField('order_clientship_address_neighborhood')
          const streetField = this.getVisibleAddressField('order_clientship_address_street')
          const streetNumberField = this.getVisibleAddressField('order_clientship_address_street_number')
          const complementField = this.getVisibleAddressField('order_clientship_address_address_complement')
          const nameField = this.getVisibleAddressField('order_clientship_address_name')
          
          if (cityField) cityField.value = ''
          if (stateField) stateField.value = ''
          if (neighborhoodField) { neighborhoodField.value = ''; neighborhoodField.setAttribute('readonly', 'readonly') }
          if (streetField) { streetField.value = ''; streetField.setAttribute('readonly', 'readonly') }
          if (streetNumberField) streetNumberField.value = ''
          if (complementField) complementField.value = ''
          if (nameField) nameField.value = ''
          
          // Garantir que campos adicionais estão ocultos
          if (additionalFields) {
            additionalFields.style.maxHeight = '0'
            additionalFields.style.opacity = '0'
            additionalFields.classList.add('hidden')
          }
          
          // Esconder mensagem de erro se estiver visível
          const errorMessage = document.getElementById('cep-error-message')
          if (errorMessage) {
            errorMessage.style.display = 'none'
          }

          // Mostrar botão "Voltar aos endereços" (o cliente tem endereços cadastrados)
          const cancelBtn = newAddressForm.querySelector('#cancel-address-form')
          if (cancelBtn) cancelBtn.closest('.mt-6')?.classList.remove('hidden')
          
          // Setup CEP field e focar
          this.setupCepField()
          // Listeners de frete e validação (blur no número + input nos campos)
          this.setupAddressFieldListeners()
          
          // Se o formulário já está preenchido (CEP + número), calcular taxa automaticamente
          setTimeout(() => {
            const hasZipcode = zipcodeField && zipcodeField.value.replace(/\D/g, '').length === 8
            const hasNumber = streetNumberField && streetNumberField.value.trim() !== ''
            
            if (hasZipcode && hasNumber) {
              console.log('[VIPTable Menu] Form already filled, calculating delivery rate automatically')
              this.calculateDeliveryRate()
            } else {
              // Focar no campo CEP apenas se não houver dados preenchidos
              if (zipcodeField) zipcodeField.focus()
            }
            
            // SEMPRE validar o botão ao abrir o formulário (detecta campos vazios ou parcialmente preenchidos)
            this.validateSendButton()
          }, 350)
        }
      })
      addAddrBtn._hasHandler = true
    }

    // clear coupon input (matches orders/_new naming)
    const couponInput = document.getElementById('checkout-coupon-code-existing') || document.querySelector('input[name="order[coupon_code]"]')
    if (couponInput) couponInput.value = ''
  }

  /**
   * Show Scene 1 (client NOT found / new client) on the standalone checkout page.
   */
  showCheckoutNew() {
    const existingEl = document.getElementById('checkout-existing')
    const newEl = document.getElementById('checkout-new')
    if (!newEl) return

    if (existingEl) existingEl.classList.add('hidden')
    newEl.classList.remove('hidden')

    const introEl = document.getElementById('checkout-intro')
    if (introEl) introEl.classList.remove('hidden')

    // ensure new-details is hidden until lookup confirms new client
    const newDetails = document.getElementById('checkout-new-details')
    if (newDetails) newDetails.classList.add('hidden')
  }

  setupCheckoutModalEventListeners() {
    const phoneInput = document.querySelector('#checkout-phone')
    const errorEl = document.querySelector('#checkout-phone-error')
    const sendBtn = document.getElementById('checkout-send-whatsapp')

    if (!phoneInput) return

    // O botão já está wired via data-action="click->companies--v2--menu#persistAndSend" no HTML.
    // NÃO adicionar outro addEventListener aqui — causaria dupla submissão do pedido.

    const formatPhone = (digits) => {
      const a = digits.slice(0,2)
      const b = digits.slice(2,7)
      const c = digits.slice(7,11)
      let out = ''
      if (a.length) out += `(${a}` + (a.length === 2 ? ') ' : '')
      if (b.length) out += b
      if (c.length) out += (c.length ? `-${c}` : '')
      return out
    }

    const stripDigits = (v) => (v || '').toString().replace(/\D/g, '')

    const validate = () => {
      const digits = stripDigits(phoneInput.value)
      const isComplete = digits.length === 11
      const thirdDigitIs9 = digits.length >= 3 ? digits[2] === '9' : false

      // hide error element safely
      if (errorEl && errorEl.classList) errorEl.classList.add('hidden')

      // protect against missing send button
      const hideSendBtn = () => { if (sendBtn) { if (sendBtn.classList) sendBtn.classList.add('hidden'); sendBtn.disabled = true } }
      const showSendBtn = () => {
        if (sendBtn) {
          // Respect the delayed-show timeout from checkout flow — don't force-show early
          if (!sendBtn._delayedShowTimeout) {
            if (sendBtn.classList) sendBtn.classList.remove('hidden')
          }
          // Não forçar enabled aqui — validateSendButton determina o estado correto
          this.validateSendButton()
        }
      }

      if (!isComplete) {
        if (errorEl && errorEl.classList) errorEl.classList.add('hidden')
        hideSendBtn()
        return false
      }

      if (!thirdDigitIs9) {
        if (errorEl) {
          errorEl.textContent = 'Número inválido — precisa ser um celular (começando com 9).'
          if (errorEl.classList) errorEl.classList.remove('hidden')
        }
        hideSendBtn()
        return false
      }

      // valid phone: show and enable the Send button
      if (errorEl && errorEl.classList) errorEl.classList.add('hidden')
      showSendBtn()

      // Show checkout footer when phone is valid
      if (this.hasCheckoutFooterTarget) {
        this.checkoutFooterTarget.classList.remove('hidden')
      }

      // If input is complete and valid, auto-submit lookup (no need to press Continue)
      if (isComplete) {
        clearTimeout(phoneInput._autoSubmitTimeout)
        phoneInput._autoSubmitTimeout = setTimeout(async () => {
          const digits = stripDigits(phoneInput.value)
          if (this._lastLookupDigits !== digits && !this._lookupPending) {
            this._lastLookupDigits = digits
            this._lookupPending = true
            try {
              await this._lookupClientshipAndHandleResponse(digits)
            } finally {
              this._lookupPending = false
            }
          }
        }, 200)
      }

      return true
    }

    phoneInput.addEventListener('input', (e) => {
      const input = e.target
      const cursorPos = input.selectionStart
      const oldValue = input.value
      const digits = stripDigits(oldValue).slice(0,11)
      const newValue = formatPhone(digits)
      input.value = newValue
      // Restaura a posição do cursor com base no número de dígitos antes dele,
      // evitando que o cursor salte para o final ao pressionar Backspace no DDD.
      const digitsBeforeCursor = stripDigits(oldValue.slice(0, cursorPos)).length
      if (digitsBeforeCursor === 0) {
        input.setSelectionRange(0, 0)
      } else {
        let digitCount = 0
        let newCursorPos = newValue.length
        for (let i = 0; i < newValue.length; i++) {
          if (/\d/.test(newValue[i])) {
            digitCount++
            if (digitCount === digitsBeforeCursor) { newCursorPos = i + 1; break }
          }
        }
        input.setSelectionRange(newCursorPos, newCursorPos)
      }
      const ok = validate()

      // Auto-submit when full 11 digits entered and valid (debounced by lastLookupDigits)
      if (digits.length === 11 && ok) {
        if (this._lastLookupDigits !== digits && !this._lookupPending) {
          this._lastLookupDigits = digits
          this._lookupPending = true
          // reuse the same continue handler logic
          ;(async () => {
            try {
              await this._lookupClientshipAndHandleResponse(digits)
            } finally {
              this._lookupPending = false
            }
          })()
        }
      }
    })

    phoneInput.addEventListener('blur', () => validate())

    // Note: sendBtn click handler removed - already wired via data-action="click->companies--v2--menu#persistAndSend" in HTML (line 1776)
    // Adding another listener here causes duplicate order submissions

    // Wire new-client payment select immediately (element is in DOM from page load, just hidden)
    this.setupNewClientPaymentSelect()
    // Wire new-client delivery select immediately
    this.setupNewClientDeliverySelect()

    // helper to lookup clientship and handle the response (used by Continue and auto-submit)
    this._lookupClientshipAndHandleResponse = async (digits) => {
      const companySlug = this.companySlugValue || window.VIPTableMenu?.companySlug
      const url = `/v2/companies/${companySlug}/clientship_by_phone`
      const csrf = document.querySelector('meta[name="csrf-token"]')?.content || ''

      try {
        const resp = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrf
          },
          body: JSON.stringify({ phone: digits })
        })

        if (resp.status === 200) {
          const json = await resp.json()

          // Persist to localStorage for later steps
          localStorage.setItem('clientship', JSON.stringify({ phone: digits, name: json.name, addresses: json.addresses }))

          // Populate clientship_id hidden field so server can find_by(uuid:)
          const clientshipIdField = document.getElementById('order_clientship_id')
          if (clientshipIdField && json.uuid) clientshipIdField.value = json.uuid

          // Fill checkout form if present
          const phoneField = document.querySelector('input[name="clientship[phone]"]')
          if (phoneField) phoneField.value = formatPhone(digits)

          const nameField = this.getVisibleAddressField('order_clientship_attributes_name') || document.querySelector('input[name="order[clientship_attributes][name]"]')
          if (nameField) nameField.value = json.name

          // Fill first address into checkout form if present
          if (json.addresses && json.addresses.length > 0) {
            const a = json.addresses[0]
            const zipcodeField = this.getVisibleAddressField('order_clientship_address_zipcode')
            const streetField = this.getVisibleAddressField('order_clientship_address_street')
            const numberField = this.getVisibleAddressField('order_clientship_address_street_number')
            const neighborhoodField = this.getVisibleAddressField('order_clientship_address_neighborhood')
            const cityField = this.getVisibleAddressField('order_clientship_address_city_name')
            const stateField = this.getVisibleAddressField('order_clientship_address_state_abbreviation')

            if (zipcodeField) zipcodeField.value = a.zipcode
            if (streetField) streetField.value = a.street
            if (numberField) numberField.value = a.street_number
            if (neighborhoodField) neighborhoodField.value = a.neighborhood
            if (cityField) cityField.value = a.city_name
            if (stateField) stateField.value = a.state_abbreviation
          }

          // If the standalone checkout page is present, switch to Scene 2 (client exists)
          try {
            this.showCheckoutClientExists({ name: json.name, phone: digits, addresses: json.addresses })
          } catch (err) {
            // non-fatal — continue with existing flow
            console.error('[VIPTable Menu] showCheckoutClientExists error', err)
          }

          this.closeCheckoutModal()

        } else if (resp.status === 404) {
          // Not found — prefill phone and, if standalone checkout present, reveal Scene 1 details
          const phoneField = document.querySelector('input[name="clientship[phone]"]')
          if (phoneField) phoneField.value = formatPhone(digits)

          // If standalone checkout page is present, reveal its "new client" details
          const newDetails = document.getElementById('checkout-new-details')
          const continueBtn = document.getElementById('checkout-continue')
          if (newDetails) {
            // ensure the standalone checkout UI is visible and focused
            try { this.showCheckoutNew() } catch (err) { /* non-fatal */ }

            // unhide details and enable Continue — but delay the button appearance by 2s
            newDetails.classList.remove('hidden')
            this.setupNewClientPaymentSelect()
            this.validateSendButton()
            if (continueBtn) {
              // ensure enabled but keep hidden until user has had time to read the form
              continueBtn.disabled = false
              continueBtn.classList.add('hidden')

              // clear prior timeouts and schedule reveal after 2s
              clearTimeout(continueBtn._delayedShowTimeout)
              continueBtn._delayedShowTimeout = setTimeout(() => {
                continueBtn.classList.remove('hidden')
              }, 2000)
            }

            this.closeCheckoutModal()
            return
          }

          // fallback: open full checkout (modal -> page)
          this.closeCheckoutModal()
          const finishBtn = document.getElementById('finish-order')
          if (finishBtn) finishBtn.click()
        } else if (resp.status === 403) {
          // CSRF blocked — still reveal the new-client details so the user can continue manually
          this.showToast('Requisição bloqueada (CSRF faltando)', 'error')
          const newDetails = document.getElementById('checkout-new-details')
          if (newDetails) {
            try { this.showCheckoutNew() } catch (err) { /* non-fatal */ }
            newDetails.classList.remove('hidden')
            this.setupNewClientPaymentSelect()
            this.validateSendButton()
            const continueBtn = document.getElementById('checkout-continue')
            if (continueBtn) {
              continueBtn.disabled = false
              clearTimeout(continueBtn._delayedShowTimeout)
              continueBtn._delayedShowTimeout = setTimeout(() => continueBtn.classList.remove('hidden'), 2000)
            }
            this.closeCheckoutModal()
            return
          }
        } else if (resp.status === 429) {
          this.showToast('Muitas tentativas. Tente novamente mais tarde.', 'warning')
        } else {
          // generic error: reveal new-client details as a fallback so the user can proceed
          this.showToast('Erro ao consultar cliente', 'error')
          const newDetails = document.getElementById('checkout-new-details')
          if (newDetails) {
            try { this.showCheckoutNew() } catch (err) { /* non-fatal */ }
            newDetails.classList.remove('hidden')
            this.setupNewClientPaymentSelect()
            this.validateSendButton()
            const continueBtn = document.getElementById('checkout-continue')
            if (continueBtn) {
              continueBtn.disabled = false
              clearTimeout(continueBtn._delayedShowTimeout)
              continueBtn._delayedShowTimeout = setTimeout(() => continueBtn.classList.remove('hidden'), 2000)
            }
            this.closeCheckoutModal()
            return
          }
        }
      } catch (err) {
        console.error('[VIPTable Menu] clientship lookup error', err)
        this.showToast('Erro de rede — tente novamente', 'error')
        // network error: reveal new-client details so manual entry can continue
        const newDetails = document.getElementById('checkout-new-details')
        if (newDetails) {
          try { this.showCheckoutNew() } catch (e) { /* non-fatal */ }
          newDetails.classList.remove('hidden')
          this.setupNewClientPaymentSelect()
          this.validateSendButton()
          const continueBtn = document.getElementById('checkout-continue')
          if (continueBtn) {
            continueBtn.disabled = false
            clearTimeout(continueBtn._delayedShowTimeout)
            continueBtn._delayedShowTimeout = setTimeout(() => continueBtn.classList.remove('hidden'), 2000)
          }
          this.closeCheckoutModal()
        }
      }
    }

    // allow closing modal with ESC
    const escHandler = (ev) => {
      if (ev.key === 'Escape') this.closeCheckoutModal()
    }
    document.addEventListener('keydown', escHandler, { once: true })
  }

  // ==========================================
  // UI EFFECTS & HELPERS
  // ==========================================

  /**
   * Configura efeitos de scroll
   */
  setupScrollEffects() {
    let lastScrollY = 0

    window.addEventListener('scroll', () => {
      const currentScrollY = window.scrollY

      // Efeito de glass no header
      if (this.hasHeaderTarget) {
        if (currentScrollY > 50) {
          this.headerTarget.classList.add('shadow-lg')
        } else {
          this.headerTarget.classList.remove('shadow-lg')
        }
      }

      lastScrollY = currentScrollY
    }, { passive: true })
  }

  /**
   * Configura observer para categorias
   */
  setupIntersectionObserver() {
    const sections = document.querySelectorAll('section[data-category]')
    
    if (sections.length === 0) return

    this.scrollObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const category = entry.target.dataset.category
          this.updateActiveCategory(category)
        }
      })
    }, {
      rootMargin: '-20% 0px -70% 0px',
      threshold: 0
    })

    sections.forEach(section => this.scrollObserver.observe(section))
  }

  /**
   * Atualiza categoria ativa na navegação
   */
  updateActiveCategory(categorySlug) {
    const pills = document.querySelectorAll('.category-pill')
    
    pills.forEach(pill => {
      if (pill.dataset.category === categorySlug) {
        pill.classList.add('active')
      } else {
        pill.classList.remove('active')
      }
    })
  }

  /**
   * Scroll suave para categoria
   */
  scrollToCategory(event) {
    event.preventDefault()
    
    const category = event.currentTarget.dataset.category
    const section = document.getElementById(`category-${category}`)
    
    if (section) {
      const headerOffset = 140
      const elementPosition = section.getBoundingClientRect().top
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      })
    }
  }

  /**
   * Aplica estado visual de fechado
   */
  applyClosedState() {
    // Add a class on the root to let CSS handle all closed-state visuals (no inline styles)
    document.body.classList.add('store-closed')
  }

  /**
   * Animação ao adicionar item
   */
  animateAddToCart(button) {
    button.classList.add('scale-125')
    setTimeout(() => {
      button.classList.remove('scale-125')
    }, 200)
  }

  /**
   * Wira o select de método de entrega do fluxo de novo cliente.
   * Pode ser chamado múltiplas vezes com segurança.
   */
  setupNewClientDeliverySelect() {
    const deliverySelectNew = document.getElementById('checkout-delivery-method-new')
    const newNoAddressEl = document.querySelector('.checkout-new-no-address')
    if (!deliverySelectNew) return

    const showAddress = () => {
      if (!newNoAddressEl) return
      newNoAddressEl.classList.remove('hidden')
      newNoAddressEl.offsetHeight // force reflow
      newNoAddressEl.style.maxHeight = '800px' // valor fixo maior que o conteúdo
      newNoAddressEl.style.opacity = '1'
      newNoAddressEl.style.overflow = 'visible'
      // Inicializar máscara CEP + listeners de cálculo de frete e validação
      this.setupCepField()
      this.setupAddressFieldListeners()
    }

    const hideAddress = () => {
      if (!newNoAddressEl) return
      newNoAddressEl.style.maxHeight = '0'
      newNoAddressEl.style.opacity = '0'
      newNoAddressEl.style.overflow = 'hidden'
      setTimeout(() => {
        if (newNoAddressEl.style.opacity === '0') newNoAddressEl.classList.add('hidden')
      }, 300)
    }

    if (!deliverySelectNew._hasDeliveryNewHandler) {
      deliverySelectNew.addEventListener('change', (ev) => {
        if (ev.target.value === 'delivery') {
          showAddress()
        } else {
          hideAddress()
        }
        this.validateSendButton()
      })
      deliverySelectNew._hasDeliveryNewHandler = true
    }

    // Aplicar estado inicial
    if (deliverySelectNew.value === 'delivery') showAddress()
    else hideAddress()
  }

  /**
   * Wira o select de pagamento do fluxo de novo cliente e aplica estado inicial.
   * Pode ser chamado múltiplas vezes com segurança (listener wired apenas 1x).
   */
  setupNewClientPaymentSelect() {
    const paymentSelect = document.getElementById('checkout-payment-method-new')
    if (!paymentSelect) return

    const showHideChangeFor = (value) => {
      const container = document.getElementById('checkout-change-for-container-new')
      if (!container) return
      if (value === 'money') {
        container.classList.remove('hidden')
        this.setupChangeForMask('checkout-change-for-new', 'checkout-change-for-error-new')
      } else {
        container.classList.add('hidden')
        // Limpar hidden para não enviar troco stale de outra sessão
        const hidden = document.getElementById('checkout-change-for-cents')
        if (hidden) hidden.value = '0'
      }
    }

    // Wire listener apenas 1x
    if (!paymentSelect._hasChangeHandler) {
      paymentSelect.addEventListener('change', (ev) => {
        showHideChangeFor(ev.target.value)
        this.validateSendButton()
      })
      paymentSelect._hasChangeHandler = true
    }

    // Sempre aplicar estado inicial (ao revelar o formulário ou na carga)
    showHideChangeFor(paymentSelect.value)
    this.validateSendButton()
  }

  /**
   * Aplica máscara de moeda brasileira no input de troco e valida mínimo
   */
  setupChangeForMask(inputId = 'checkout-change-for-existing', errorId = 'checkout-change-for-error') {
    const input = document.getElementById(inputId)
    const hiddenInput = document.getElementById('checkout-change-for-cents')
    const errorEl = document.getElementById(errorId)
    if (!input || input._hasMaskHandler) return

    const parseCents = (raw) => parseInt(raw.replace(/\D/g, '') || '0', 10)
    const fmtBRL = (cents) => cents > 0
      ? (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
      : ''

    const validate = (cents) => {
      const subtotal = this.cart.reduce((s, it) => s + (parseInt(it.price, 10) * parseInt(it.quantity, 10)), 0)
      const rate = parseInt(document.getElementById('delivery_rate_field')?.value || '0', 10)
      const total = subtotal + rate
      if (errorEl) {
        if (cents > 0 && cents < total) {
          errorEl.textContent = `O troco deve ser para um valor acima de ${this.formatCurrency(total)}`
          errorEl.classList.remove('hidden')
        } else {
          errorEl.classList.add('hidden')
        }
      }
    }

    input.addEventListener('input', () => {
      const cents = parseCents(input.value)
      input.value = fmtBRL(cents)
      // O banco armazena change_for em reais inteiros (ex: 300 = R$ 300,00)
      // O painel exibe com `change_for * 100`, então gravamos reais aqui
      if (hiddenInput) hiddenInput.value = Math.round(cents / 100)
      validate(cents)
      this.validateSendButton()
    })

    input.addEventListener('focus', () => {
      // Move cursor para o final
      const len = input.value.length
      setTimeout(() => input.setSelectionRange(len, len), 0)
    })

    input.addEventListener('blur', () => {
      const cents = parseCents(input.value)
      input.value = fmtBRL(cents)
    })

    input._hasMaskHandler = true
  }

  /**
   * Normaliza número de WhatsApp garantindo prefixo 55 (Brasil).
   * Aceita: "41999999999", "5541999999999", "(41) 9 9999-9999" etc.
   * Sempre retorna apenas dígitos com 55 na frente.
   */
  _waPhone(raw) {
    const digits = (raw || '').toString().replace(/\D/g, '')
    if (!digits) return digits
    return digits.startsWith('55') ? digits : '55' + digits
  }

  /**
   * Escapa caracteres HTML para prevenir XSS
   */
  escapeHtml(str) {
    if (str == null) return ''
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
  }

  /**
   * Formata valor em moeda brasileira
   */
  formatCurrency(valueInCents) {
    const value = valueInCents / 100
    return value.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    })
  }

  /**
   * Modal de confirmação para remoção de item do carrinho
   */
  _confirmRemoval(itemName, onConfirm) {
    // Remover modal existente se houver
    const existing = document.getElementById('viptable-confirm-removal')
    if (existing) existing.remove()

    // Guardar o elemento que tinha foco antes de abrir o modal
    const previouslyFocused = document.activeElement

    const overlay = document.createElement('div')
    overlay.id = 'viptable-confirm-removal'
    overlay.setAttribute('role', 'dialog')
    overlay.setAttribute('aria-modal', 'true')
    overlay.setAttribute('aria-label', 'Confirmar remoção de item')
    overlay.style.cssText = 'position:fixed;inset:0;z-index:9999;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,0.6);backdrop-filter:blur(4px);'

    const card = document.createElement('div')
    card.style.cssText = 'background:var(--theme-bg-card,#1a1a2e);border:1px solid var(--theme-border,#333);border-radius:1rem;padding:1.5rem;max-width:20rem;width:90%;text-align:center;box-shadow:0 8px 32px rgba(0,0,0,0.4);'

    card.innerHTML = `
      <p style="color:var(--theme-text,#fff);font-size:0.95rem;margin-bottom:1.25rem;line-height:1.4;">Deseja mesmo remover <strong>${this.escapeHtml(itemName)}</strong> do carrinho?</p>
      <div style="display:flex;gap:0.75rem;justify-content:center;">
        <button id="confirm-removal-cancel" style="flex:1;padding:0.6rem;border-radius:0.5rem;border:1px solid var(--theme-border,#444);background:transparent;color:var(--theme-text-muted,#aaa);font-size:0.85rem;cursor:pointer;">Cancelar</button>
        <button id="confirm-removal-ok" style="flex:1;padding:0.6rem;border-radius:0.5rem;border:none;background:#ef4444;color:#fff;font-size:0.85rem;font-weight:600;cursor:pointer;">Remover</button>
      </div>
    `

    overlay.appendChild(card)
    document.body.appendChild(overlay)

    const close = () => {
      overlay.remove()
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus()
      }
    }

    const cancelBtn = document.getElementById('confirm-removal-cancel')
    const okBtn = document.getElementById('confirm-removal-ok')

    cancelBtn.addEventListener('click', close)
    okBtn.addEventListener('click', () => {
      close()
      onConfirm()
    })
    overlay.addEventListener('click', (e) => { if (e.target === overlay) close() })

    // Fechar com Escape e trap de foco entre os dois botões
    overlay.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); close(); return }
      if (e.key === 'Tab') {
        const focusable = [cancelBtn, okBtn]
        const first = focusable[0], last = focusable[focusable.length - 1]
        if (e.shiftKey) {
          if (document.activeElement === first) { e.preventDefault(); last.focus() }
        } else {
          if (document.activeElement === last) { e.preventDefault(); first.focus() }
        }
      }
    })

    // Foco inicial no botão "Cancelar"
    cancelBtn.focus()
  }

  /**
   * Overlay de loading exibido enquanto o pedido é processado
   */
  _showOrderLoadingOverlay() {
    if (document.getElementById('viptable-order-loading')) return

    const overlay = document.createElement('div')
    overlay.id = 'viptable-order-loading'
    overlay.style.cssText = 'position:fixed;inset:0;z-index:10000;display:flex;flex-direction:column;align-items:center;justify-content:center;background:var(--theme-bg,#0a0a1a);transition:opacity 0.3s;'

    overlay.innerHTML = `
      <div style="position:relative;width:96px;height:96px;margin-bottom:2rem;">
        <svg viewBox="0 0 96 96" fill="none" xmlns="http://www.w3.org/2000/svg" style="width:96px;height:96px;">
          <!-- Prato (base) -->
          <ellipse cx="48" cy="58" rx="38" ry="12" fill="var(--theme-bg-card,#1a1a2e)" stroke="var(--theme-border,#333)" stroke-width="1.5"/>
          <!-- Tampa do prato (cloche) -->
          <path d="M16 58 C16 32, 80 32, 80 58" fill="none" stroke="var(--theme-accent,#D4AF37)" stroke-width="2" stroke-linecap="round"/>
          <!-- Puxador -->
          <ellipse cx="48" cy="32" rx="6" ry="3" fill="none" stroke="var(--theme-accent,#D4AF37)" stroke-width="1.5"/>
          <!-- Vapor animado -->
          <g style="animation:viptableVapor 2s ease-in-out infinite;">
            <path d="M36 26 Q34 18 38 12" stroke="var(--theme-text-muted,#888)" stroke-width="1.5" stroke-linecap="round" fill="none" opacity="0.6"/>
            <path d="M48 24 Q46 14 50 8" stroke="var(--theme-text-muted,#888)" stroke-width="1.5" stroke-linecap="round" fill="none" opacity="0.8"/>
            <path d="M60 26 Q58 18 62 12" stroke="var(--theme-text-muted,#888)" stroke-width="1.5" stroke-linecap="round" fill="none" opacity="0.6"/>
          </g>
        </svg>
        <!-- Contorno circular girando -->
        <svg viewBox="0 0 96 96" style="position:absolute;inset:0;width:96px;height:96px;animation:viptableSpin 1.8s linear infinite;">
          <circle cx="48" cy="48" r="46" fill="none" stroke="var(--theme-accent,#D4AF37)" stroke-width="2" stroke-dasharray="72 216" stroke-linecap="round" opacity="0.7"/>
        </svg>
      </div>
      <p style="color:var(--theme-text-heading,#fff);font-size:1.1rem;font-weight:600;font-family:'Lato',sans-serif;margin-bottom:0.5rem;">Processando seu pedido...</p>
      <p style="color:var(--theme-text-muted,#aaa);font-size:0.875rem;">Aguarde um instante</p>
      <style>
        @keyframes viptableSpin { to { transform: rotate(360deg); } }
        @keyframes viptableVapor { 0%,100% { transform: translateY(0); opacity: 0.5; } 50% { transform: translateY(-6px); opacity: 1; } }
      </style>
    `

    document.body.appendChild(overlay)
  }

  _removeOrderLoadingOverlay() {
    const overlay = document.getElementById('viptable-order-loading')
    if (overlay) {
      overlay.style.opacity = '0'
      setTimeout(() => overlay.remove(), 300)
    }
  }

  /**
   * Mostra toast notification
   */
  showToast(message, type = 'info') {
    // Remover toast existente
    const existingToast = document.querySelector('.viptable-toast')
    if (existingToast) {
      existingToast.remove()
    }

    const colors = {
      success: 'bg-emerald-500',
      warning: 'bg-amber-500',
      error: 'bg-red-500',
      info: ''
    }

    const toast = document.createElement('div')
    toast.className = `viptable-toast fixed bottom-24 left-1/2 transform -translate-x-1/2 px-6 py-3 rounded-full ${colors[type]} text-sm font-medium shadow-2xl z-50 transition-all duration-300 opacity-0 translate-y-4`
    if (type === 'info') {
      toast.classList.add('toast-accent')
    } else {
      toast.classList.add('text-white')
    }
    toast.textContent = message
    
    document.body.appendChild(toast)

    // Animar entrada
    requestAnimationFrame(() => {
      toast.classList.remove('opacity-0', 'translate-y-4')
      toast.classList.add('opacity-100', 'translate-y-0')
    })

    // Remover após 3 segundos
    setTimeout(() => {
      toast.classList.add('opacity-0', 'translate-y-4')
      toast.classList.remove('opacity-100', 'translate-y-0')
      setTimeout(() => toast.remove(), 300)
    }, 3000)
  }

  // ==========================================
  // CATEGORY SYNC ON SCROLL (MOBILE)
  // ==========================================

  /**
   * Setup da sincronização de categoria ativa ao fazer scroll
   */
  setupCategorySyncOnScroll() {
    // Busca as seções de categoria no main
    this.categorySections = document.querySelectorAll('section[data-category]')
    
    console.log('[VIPTable Menu] Category sync setup:', {
      hasCategoryNavTarget: this.hasCategoryNavTarget,
      hasCategoryScrollerTarget: this.hasCategoryScrollerTarget,
      sectionsFound: this.categorySections.length
    })

    if (!this.hasCategoryNavTarget || this.categorySections.length === 0) {
      console.warn('[VIPTable Menu] Category sync aborted - missing targets')
      return
    }

    // Bind handlers
    this.categoryScrollHandler = this.syncCategoryScroll.bind(this)

    window.addEventListener('scroll', this.categoryScrollHandler, { passive: true })

    // Executa uma vez no carregamento
    this.syncCategoryScroll()
  }

  /**
   * Sincroniza qual categoria está ativa baseado no scroll
   */
  syncCategoryScroll() {
    if (!this.hasCategoryNavTarget) return

    // Encontra qual seção está mais visível
    let visibleCategory = null
    const viewportMiddle = window.innerHeight / 2

    this.categorySections.forEach(section => {
      const rect = section.getBoundingClientRect()
      // Verifica se a seção está visível na metade da viewport
      if (rect.top < viewportMiddle && rect.bottom > viewportMiddle) {
        visibleCategory = section.getAttribute('data-category')
      }
    })

    if (visibleCategory) {
      // Encontra o link da categoria no menu
      const categoryLink = this.categoryNavTarget.querySelector(
        `a.category-pill[data-category="${visibleCategory}"]`
      )

      if (categoryLink && !categoryLink.classList.contains('active')) {
        // Remove classe active de todos
        this.categoryNavTarget.querySelectorAll('a.category-pill').forEach(link => {
          link.classList.remove('active')
        })

        // Adiciona classe active ao link atual
        categoryLink.classList.add('active')

        // Faz scroll horizontal para mostrar a categoria ativa
        if (this.hasCategoryScrollerTarget) {
          this.scrollCategoryIntoView(categoryLink)
        }
      }
    }
  }

  /**
   * Faz scroll horizontal do menu de categorias para mostrar item ativo
   */
  scrollCategoryIntoView(element) {
    if (!this.hasCategoryScrollerTarget) return
    
    const container = this.categoryScrollerTarget
    const containerRect = container.getBoundingClientRect()
    const elementRect = element.getBoundingClientRect()
    
    // Calcula a posição central
    const elementCenter = elementRect.left + (elementRect.width / 2)
    const containerCenter = containerRect.left + (containerRect.width / 2)
    const scrollOffset = elementCenter - containerCenter
    
    container.scrollBy({
      left: scrollOffset,
      behavior: 'smooth'
    })
  }

  // ==========================================
  // FOOD DETAIL MODAL
  // ==========================================

  /**
   * Abre modal de detalhes do prato
   */
  async openFoodDetail(event) {
    const card = event.currentTarget
    const foodSlug = card.dataset.foodSlug

    if (!foodSlug) {
      console.error('[VIPTable Menu] Food slug not found')
      return
    }

    console.log('[VIPTable Menu] Opening food detail:', foodSlug)

    // Mostrar modal com loading
    this.showFoodDetailModal(null, true)

    try {
      const response = await fetch(`/${this.companySlugValue}/foods/${foodSlug}/details`)
      const foodData = await response.json()

      if (!response.ok) {
        throw new Error('Failed to load food details')
      }

      console.log('[VIPTable Menu] Food data loaded:', foodData)
      
      // Armazenar dados do prato atual
      this.currentFoodData = foodData
      this.currentFoodQuantity = 1
      this.currentFoodSelections = {
        priceVariation: null,
        additionals: [],
        flavours: [],
        optionals: [],
        observations: ''
      }

      // Renderizar modal com dados
      this.showFoodDetailModal(foodData, false)
      
    } catch (error) {
      console.error('[VIPTable Menu] Error loading food details:', error)
      this.showToast('Erro ao carregar detalhes do prato', 'error')
      this.closeFoodDetailModal()
    }
  }

  // Wrapper used by small "Escolher" buttons which are inside the card
  // The button itself doesn't carry data-food-slug, so we find the enclosing card
  openFoodDetailFromButton(event) {
    event.preventDefault()
    event.stopPropagation()
    const button = event.currentTarget
    const card = button.closest('article.food-card')
    if (!card) {
      console.error('[VIPTable Menu] Cannot find parent food card for choose button')
      return
    }

    // Reuse existing handler by faking an event with currentTarget = card
    try {
      this.openFoodDetail({ currentTarget: card })
    } catch (e) {
      console.error('[VIPTable Menu] openFoodDetailFromButton error', e)
    }
  }

  /**
   * Renderiza o modal de detalhes
   */
  showFoodDetailModal(foodData, isLoading) {
    const existingModal = document.querySelector('#food-detail-modal')
    if (existingModal) {
      existingModal.remove()
    }

    const modal = document.createElement('div')
    modal.id = 'food-detail-modal'
    modal.className = 'fixed inset-0 z-[100] flex items-end sm:items-center justify-center'
    modal.innerHTML = isLoading ? this.renderLoadingModal() : this.renderFoodModal(foodData)

    document.body.appendChild(modal)
    document.body.classList.add('no-scroll')

    // Animar entrada
    requestAnimationFrame(() => {
      modal.classList.add('active')
    })

    // Event listeners
    this.setupModalEventListeners()
  }

  /**
   * Renderiza loading state
   */
  renderLoadingModal() {
    return `
      <div class="modal-backdrop fixed inset-0 backdrop-blur-sm transition-opacity duration-300 opacity-0"  data-action="click->companies--v2--menu#closeFoodDetailModal"></div>
      <div class="modal-content relative rounded-t-3xl sm:rounded-2xl w-full sm:max-w-2xl max-h-[90vh] overflow-hidden transform transition-all duration-300 translate-y-full sm:translate-y-0 sm:scale-95 sm:opacity-0 shadow-2xl" >
        <div class="p-8 text-center">
          <div class="animate-pulse space-y-4">
            <div class="h-64 rounded-xl" ></div>
            <div class="h-8 rounded w-3/4 mx-auto" ></div>
            <div class="h-4 rounded w-full" ></div>
            <div class="h-4 rounded w-5/6" ></div>
          </div>
        </div>
      </div>
    `
  }

  /**
   * Renderiza modal completo com dados do prato
   */
  renderFoodModal(food) {
    return `
      <div class="modal-backdrop fixed inset-0 backdrop-blur-sm transition-opacity duration-300 opacity-0"  data-action="click->companies--v2--menu#closeFoodDetailModal"></div>
      
      <!-- Desktop: tela cheia com 2 colunas | Tablet: tela cheia 1 coluna | Mobile: modal inferior -->
      <div class="modal-content fixed inset-0 md:inset-8 lg:inset-16 sm:rounded-t-3xl lg:rounded-2xl flex flex-col transform transition-all duration-300 translate-y-full sm:translate-y-0 sm:scale-95 sm:opacity-0 shadow-2xl overflow-hidden" >
        
        <!-- Botão fechar FIXO -->
        <button class="fixed sm:absolute top-4 right-4 z-20 w-10 h-10 rounded-full backdrop-blur-sm flex items-center justify-center transition-colors"  data-action="click->companies--v2--menu#closeFoodDetailModal:stop">
          <svg class="w-6 h-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>

        <!-- Layout: Mobile - vertical | Tablet/Desktop - horizontal -->
        <div class="flex flex-col lg:flex-row h-full overflow-hidden">
          
          <!-- IMAGEM DESKTOP (lado esquerdo fixo, ocupa 40%) -->
          <div class="hidden lg:block lg:w-2/5 flex-shrink-0 relative lg:h-full">
            ${food.picture_url ? 
              `<img src="${food.picture_url}" alt="${this.escapeHtml(food.name)}" class="w-full h-full object-cover">` :
              `<div class="w-full h-full flex items-center justify-center" >
                <svg class="w-20 h-20"  xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                </svg>
              </div>`
            }
            <div class="absolute inset-0" ></div>
          </div>

          <!-- CONTEÚDO (Desktop: lado direito, ocupa 60% | Mobile: tudo) -->
          <div class="flex-1 lg:w-3/5 flex flex-col h-full overflow-hidden">
            
            <!-- Conteúdo scrollável (Mobile: inclui imagem) -->
            <div class="flex-1 overflow-y-auto" >
              
              <!-- IMAGEM MOBILE/TABLET (scrollável) -->
              <div class="lg:hidden relative h-64 sm:h-80 flex-shrink-0">
                ${food.picture_url ? 
                  `<img src="${food.picture_url}" alt="${this.escapeHtml(food.name)}" class="w-full h-full object-cover">` :
                  `<div class="w-full h-full flex items-center justify-center" >
                    <svg class="w-20 h-20"  xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                    </svg>
                  </div>`
                }
                <div class="absolute inset-0" ></div>
              </div>

              <!-- Conteúdo do prato -->
              <div class="p-6 space-y-6">
                
                <!-- Nome e descrição -->
                <div>
                  <h2 class="text-3xl font-bold mb-2" >${this.escapeHtml(food.name)}</h2>
                  ${food.description ? `<p class="leading-relaxed" >${this.escapeHtml(food.description)}</p>` : ''}
                </div>

                ${this.renderPriceSection(food)}
                ${this.renderAdditionalsSection(food)}
                ${this.renderOptionsSection(food)}
                ${this.renderObservationsSection()}

              </div>

            </div>

            <!-- Footer fixo -->
            <div class="flex-shrink-0 p-4 sm:p-6 space-y-3 sm:space-y-4" >
              ${this.renderQuantityControls()}
              ${this.renderAddButton(food)}
            </div>

          </div>

        </div>

      </div>
    `
  }

  /**
   * Renderiza seção de preço (fixo, variável ou pizza)
   */
  renderPriceSection(food) {
    if (food.is_pizza && food.price_calculated_by_additionals) {
      // Pizza com cálculo por sabores
      return `
        <div class="p-4 rounded-xl" >
          <h3 class="font-semibold mb-2" >
            ${food.global_additionals_limit === 1 ? 'Selecione 1 sabor' : `Selecione até ${food.global_additionals_limit} sabores`}
          </h3>
          <p class="text-sm" >O preço será calculado pela média dos sabores selecionados</p>
        </div>
      `
    } else if (food.variable_price && food.price_variations && food.price_variations.length > 0) {
      // Preço variável
      return `
        <div class="space-y-3">
          <div class="flex items-center gap-2">
            <h3 class="font-semibold" >Opções</h3>
            <span class="px-2 py-1 text-xs font-semibold rounded-full" >Seleção Obrigatória</span>
          </div>
          <div class="space-y-2">
            ${food.price_variations.map((variation, index) => `
              <label class="flex items-center justify-between p-4 rounded-xl cursor-pointer transition-colors" >
                <div class="flex items-center gap-3">
                  <input type="radio" name="price_variation" value="${index}" data-price="${variation.price}" data-text="${this.escapeHtml(variation.name)}" class="w-5 h-5" ${index === 0 ? 'checked' : ''}>
                  <span class="font-medium" >${this.escapeHtml(variation.name)}</span>
                </div>
                <span class="font-semibold" >${this.formatCurrency(variation.price)}</span>
              </label>
            `).join('')}
          </div>
        </div>
      `
    } else {
      // Preço fixo
      return `
        <div class="p-4 rounded-xl" >
          <div class="flex items-center justify-between">
            <span >Preço</span>
            <span class="text-2xl font-semibold" >${this.formatCurrency(food.price)}</span>
          </div>
        </div>
      `
    }
  }

  /**
   * Renderiza blocos de adicionais
   */
  renderAdditionalsSection(food) {
    if (!food.additionals || food.additionals.length === 0) {
      return ''
    }

    return food.additionals.map((block, blockIndex) => `
      <div class="space-y-3" data-additional-block="${blockIndex}">
        <div class="flex items-center gap-2">
          <h3 class="font-semibold" >${this.escapeHtml(block.question)}</h3>
          ${block.required_block ? '<span class="px-2 py-1 text-xs font-semibold rounded-full" >Seleção Obrigatória</span>' : ''}
        </div>
        ${block.limit && block.limit !== 'Sem limites' ? `<p class="text-sm" >Limite: ${block.limit} ${block.limit == 1 ? 'item' : 'itens'}</p>` : ''}
        <div class="space-y-2">
          ${block.items.map((item, itemIndex) => `
            <label class="flex items-center justify-between p-4 rounded-xl cursor-pointer transition-colors" >
              <div class="flex items-center gap-3">
                <input 
                  type="checkbox" 
                  name="additional_${blockIndex}_${itemIndex}" 
                  data-block-index="${blockIndex}"
                  data-item-index="${itemIndex}"
                  data-price="${item.price}"
                  data-text="${this.escapeHtml(item.name)}"
                  data-compound="${block.compound_price}"
                  class="w-5 h-5 additional-checkbox ${block.compound_price === 'true' ? 'compound-additional' : 'normal-additional'}"
                  
                  data-action="change->companies--v2--menu#handleAdditionalChange">
                <span class="font-medium" >${this.escapeHtml(item.name)}</span>
              </div>
              <span class="font-semibold" >${item.price > 0 ? (block.compound_price === 'true' ? this.formatCurrency(item.price) : '+ ' + this.formatCurrency(item.price)) : 'Grátis'}</span>
            </label>
          `).join('')}
        </div>
      </div>
    `).join('')
  }

  /**
   * Renderiza blocos de opcionais
   */
  renderOptionsSection(food) {
    if (!food.options || food.options.length === 0) {
      return ''
    }

    return food.options.map((block, blockIndex) => `
      <div class="space-y-3">
        <h3 class="font-semibold" >${this.escapeHtml(block.question)}</h3>
        <div class="space-y-2">
          ${block.options.map((option, optIndex) => `
            <label class="flex items-center p-4 rounded-xl cursor-pointer transition-colors" >
              <input 
                type="checkbox" 
                name="optional_${blockIndex}_${optIndex}" 
                data-text="${this.escapeHtml(option)}"
                class="w-5 h-5 mr-3"
                
                data-action="change->companies--v2--menu#handleOptionalChange">
              <span class="font-medium" >${this.escapeHtml(option)}</span>
            </label>
          `).join('')}
        </div>
      </div>
    `).join('')
  }

  /**
   * Renderiza seção de observações
   */
  renderObservationsSection() {
    return `
      <div class="space-y-3">
        <h3 class="font-semibold" >Observações</h3>
        <textarea 
          id="food-observations" 
          rows="3" 
          class="observations-textarea w-full p-4 rounded-xl focus:outline-none transition-colors resize-none"
          
          placeholder="Alguma observação especial?"
          data-action="input->companies--v2--menu#handleObservationsChange"></textarea>
      </div>
    `
  }

  /**
   * Renderiza controles de quantidade
   */
  renderQuantityControls() {
    return `
      <div class="flex items-center justify-center gap-3 sm:gap-4">
        <button class="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white bg-opacity-10 flex items-center justify-center hover:bg-opacity-20 transition-colors" data-action="click->companies--v2--menu#decrementFoodQuantity">
          <svg class="w-5 h-5 sm:w-6 sm:h-6"  xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 12H4"/>
          </svg>
        </button>
        
        <span class="text-xl sm:text-2xl font-semibold w-10 sm:w-12 text-center"  id="modal-quantity">${this.currentFoodQuantity || 1}</span>
        
        <button class="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-colors"  data-action="click->companies--v2--menu#incrementFoodQuantity">
          <svg class="w-5 h-5 sm:w-6 sm:h-6"  xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
          </svg>
        </button>
      </div>
    `
  }

  /**
   * Renderiza botão de adicionar
   */
  renderAddButton(food) {
    // Considera fechado se o dado da API indica fechado OU se o Stimulus
    // value (setado no HTML no momento do render) indica fechado.
    const isDisabled = !food.company_open || !this.companyOpenValue
    const buttonText = (food.company_open && this.companyOpenValue) ? 'Adicionar ao Carrinho' : 'ESTAMOS FECHADOS'
    
    return `
      <button 
        id="modal-add-button"
        class="w-full py-3 sm:py-4 rounded-xl font-semibold text-base sm:text-lg transition-all ${
          isDisabled 
            ? 'cursor-not-allowed theme-btn-disabled' 
            : 'theme-btn-accent'
        }"
        ${isDisabled ? 'disabled' : ''}
        data-action="click->companies--v2--menu#addFoodFromModal">
        ${buttonText}
      </button>
      ${!food.company_open ? '' : '<div id="modal-validation-warning" class="hidden text-center text-red-400 text-sm mt-2"></div>'}
    `
  }

  /**
   * Fecha o modal
   */
  closeFoodDetailModal(event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
    
    console.log('[VIPTable Menu] Closing modal')
    
    const modal = document.querySelector('#food-detail-modal')
    if (!modal) {
      console.warn('[VIPTable Menu] Modal not found')
      return
    }

    // Animar saída
    modal.classList.remove('active')
    
    setTimeout(() => {
      modal.remove()
      document.body.classList.remove('no-scroll')
      this.currentFoodData = null
      this.currentFoodQuantity = 1
      this.currentFoodSelections = null
      console.log('[VIPTable Menu] Modal closed')
    }, 300)
  }

  /**
   * Setup de event listeners do modal
   */
  setupModalEventListeners() {
    // Adicionar event listeners manualmente aos elementos do modal
    const closeButtons = document.querySelectorAll('[data-action*="closeFoodDetailModal"]')
    closeButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault()
        e.stopPropagation()
        console.log('[VIPTable Menu] Close button clicked')
        this.closeFoodDetailModal(e)
      })
    })

    // Backdrop click
    const backdrop = document.querySelector('.modal-backdrop')
    if (backdrop) {
      backdrop.addEventListener('click', (e) => {
        console.log('[VIPTable Menu] Backdrop clicked')
        this.closeFoodDetailModal(e)
      })
    }

    // Radio buttons de variação de preço
    const priceVariationRadios = document.querySelectorAll('input[name="price_variation"]')
    priceVariationRadios.forEach(radio => {
      radio.addEventListener('change', (e) => this.handlePriceVariationChange(e))
    })

    // Checkboxes de adicionais
    const modalEl = document.getElementById('food-detail-modal')
    const additionalCheckboxes = (modalEl || document).querySelectorAll('.additional-checkbox')
    additionalCheckboxes.forEach(checkbox => {
      checkbox.addEventListener('change', (e) => this.handleAdditionalChange(e))
    })

    // Checkboxes de opcionais
    const optionalCheckboxes = (modalEl || document).querySelectorAll('input[type="checkbox"][name^="optional_"]')
    optionalCheckboxes.forEach(cb => {
      cb.addEventListener('change', (e) => this.handleOptionalChange(e))
    })

    // Textarea de observações
    const observationsTextarea = document.querySelector('.observations-textarea')
    if (observationsTextarea) {
      observationsTextarea.addEventListener('input', (e) => this.handleObservationsChange(e))
    }

    // Botões de quantidade
    const decrementBtn = document.querySelector('[data-action*="decrementFoodQuantity"]')
    const incrementBtn = document.querySelector('[data-action*="incrementFoodQuantity"]')
    if (decrementBtn) {
      decrementBtn.addEventListener('click', (e) => {
        e.preventDefault()
        this.decrementFoodQuantity()
      })
    }
    if (incrementBtn) {
      incrementBtn.addEventListener('click', (e) => {
        e.preventDefault()
        this.incrementFoodQuantity()
      })
    }

    // Botão de adicionar ao carrinho
    const addButton = document.querySelector('[data-action*="addFoodFromModal"]')
    if (addButton) {
      addButton.addEventListener('click', (e) => {
        e.preventDefault()
        this.addFoodFromModal()
      })
    }

    // Animar entrada após render
    setTimeout(() => {
      const content = document.querySelector('.modal-content')
      
      if (backdrop) backdrop.classList.add('opacity-100')
      if (content) {
        content.classList.remove('translate-y-full', 'sm:translate-y-0', 'sm:scale-95', 'sm:opacity-0')
        content.classList.add('translate-y-0', 'sm:scale-100', 'sm:opacity-100')
      }
    }, 10)

    // ESC para fechar
    const escapeHandler = (e) => {
      if (e.key === 'Escape') {
        console.log('[VIPTable Menu] ESC pressed')
        this.closeFoodDetailModal()
        document.removeEventListener('keydown', escapeHandler)
      }
    }
    document.addEventListener('keydown', escapeHandler)

    // Inicializar seleções
    if (this.currentFoodData) {
      this.initializeModalSelections()
    }
  }

  /**
   * Inicializa seleções baseado no tipo de prato
   */
  initializeModalSelections() {
    const food = this.currentFoodData

    // Se tem variação de preço, seleciona a primeira
    if (food.variable_price && food.price_variations && food.price_variations.length > 0) {
      this.currentFoodSelections.priceVariation = {
        price: parseInt(food.price_variations[0].price),
        text: food.price_variations[0].name
      }
    }

    // Calcular preço inicial
    this.calculateAndUpdateFoodPrice()
    this.validateModalForm()
  }

  // ==========================================
  // MODAL EVENT HANDLERS
  // ==========================================

  /**
   * Handler para mudança de variação de preço
   */
  handlePriceVariationChange(event) {
    const radio = event.currentTarget
    this.currentFoodSelections.priceVariation = {
      price: parseInt(radio.dataset.price),
      text: radio.dataset.text
    }
    this.calculateAndUpdateFoodPrice()
    this.validateModalForm()
  }

  /**
   * Handler para mudança de adicionais
   */
  handleAdditionalChange(event) {
    const checkbox = event.currentTarget
    const blockIndex = parseInt(checkbox.dataset.blockIndex)
    const isCompound = checkbox.dataset.compound === 'true'

    console.log('[VIPTable Menu] Additional changed:', {
      blockIndex,
      isCompound,
      checked: checkbox.checked,
      text: checkbox.dataset.text,
      price: checkbox.dataset.price
    })

    // Verificar limite do bloco
    if (checkbox.checked) {
      const block = this.currentFoodData.additionals[blockIndex]
      const limit = block.limit

      if (limit && limit !== 'Sem limites') {
        const modalScope = document.getElementById('food-detail-modal') || document
        const checkedInBlock = modalScope.querySelectorAll(`input[data-block-index="${blockIndex}"]:checked`)
        
        if (checkedInBlock.length > parseInt(limit)) {
          checkbox.checked = false
          this.showToast(`Limite de ${limit} ${limit == 1 ? 'item' : 'itens'} atingido`, 'warning')
          return
        }
      }

      // Para pizzas, verificar limite global
      if (isCompound && this.currentFoodData.is_pizza) {
        const globalLimit = this.currentFoodData.global_additionals_limit
        const modalScope = document.getElementById('food-detail-modal') || document
        const allFlavours = modalScope.querySelectorAll('.compound-additional:checked')
        
        if (allFlavours.length > globalLimit) {
          checkbox.checked = false
          this.showToast(`Limite de ${globalLimit} ${globalLimit == 1 ? 'sabor' : 'sabores'} atingido`, 'warning')
          return
        }
      }
    }

    // Atualizar seleções
    this.updateCurrentSelections()
    this.calculateAndUpdateFoodPrice()
    this.validateModalForm()
    this.updateAdditionalBlockVisuals(blockIndex, isCompound)
  }

  /**
   * Atualiza visualmente os itens bloqueados de um bloco de adicionais quando o limite é atingido
   */
  updateAdditionalBlockVisuals(blockIndex, isCompound) {
    // Bloqueio visual por limite do bloco
    const block = this.currentFoodData.additionals && this.currentFoodData.additionals[blockIndex]
    if (block && block.limit && block.limit !== 'Sem limites') {
      const limitNum = parseInt(block.limit)
      const modalScope2 = document.getElementById('food-detail-modal') || document
      const allInBlock = modalScope2.querySelectorAll(`input.additional-checkbox[data-block-index="${blockIndex}"]`)
      const checkedCount = Array.from(allInBlock).filter(cb => cb.checked).length
      const limitReached = checkedCount >= limitNum

      allInBlock.forEach(cb => {
        const label = cb.closest('label')
        if (cb.checked) {
          cb.disabled = false
          if (label) {
            label.classList.remove('opacity-40', 'cursor-not-allowed')
            label.classList.add('cursor-pointer')
          }
        } else {
          cb.disabled = limitReached
          if (label) {
            if (limitReached) {
              label.classList.add('opacity-40', 'cursor-not-allowed')
              label.classList.remove('cursor-pointer')
            } else {
              label.classList.remove('opacity-40', 'cursor-not-allowed')
              label.classList.add('cursor-pointer')
            }
          }
        }
      })
    }

    // Bloqueio visual para limite global de pizza (adicionais compostos)
    if (isCompound && this.currentFoodData.is_pizza && this.currentFoodData.global_additionals_limit) {
      const globalLimit = parseInt(this.currentFoodData.global_additionals_limit)
      const modalScope3 = document.getElementById('food-detail-modal') || document
      const allFlavours = modalScope3.querySelectorAll('input.compound-additional')
      const checkedCount = Array.from(allFlavours).filter(cb => cb.checked).length
      const limitReached = checkedCount >= globalLimit

      allFlavours.forEach(cb => {
        const label = cb.closest('label')
        if (cb.checked) {
          cb.disabled = false
          if (label) {
            label.classList.remove('opacity-40', 'cursor-not-allowed')
            label.classList.add('cursor-pointer')
          }
        } else {
          cb.disabled = limitReached
          if (label) {
            if (limitReached) {
              label.classList.add('opacity-40', 'cursor-not-allowed')
              label.classList.remove('cursor-pointer')
            } else {
              label.classList.remove('opacity-40', 'cursor-not-allowed')
              label.classList.add('cursor-pointer')
            }
          }
        }
      })
    }
  }

  /**
   * Handler para mudança de opcionais
   */
  handleOptionalChange(event) {
    this.updateCurrentSelections()
  }

  /**
   * Handler para mudança de observações
   */
  handleObservationsChange(event) {
    this.currentFoodSelections.observations = event.currentTarget.value
  }

  /**
   * Incrementa quantidade no modal
   */
  incrementFoodQuantity() {
    this.currentFoodQuantity += 1
    this.updateQuantityDisplay()
    this.calculateAndUpdateFoodPrice()
  }

  /**
   * Decrementa quantidade no modal
   */
  decrementFoodQuantity() {
    if (this.currentFoodQuantity > 1) {
      this.currentFoodQuantity -= 1
      this.updateQuantityDisplay()
      this.calculateAndUpdateFoodPrice()
    }
  }

  /**
   * Atualiza display de quantidade
   */
  updateQuantityDisplay() {
    const quantityEl = document.getElementById('modal-quantity')
    if (quantityEl) {
      quantityEl.textContent = this.currentFoodQuantity
    }
  }

  /**
   * Atualiza seleções atuais baseado no formulário
   */
  updateCurrentSelections() {
    const modal = document.getElementById('food-detail-modal')
    if (!modal) return

    // Adicionais normais
    const normalAdditionals = modal.querySelectorAll('.normal-additional:checked')
    this.currentFoodSelections.additionals = Array.from(normalAdditionals).map(cb => ({
      text: cb.dataset.text,
      price: parseInt(cb.dataset.price, 10) || 0
    }))

    // Sabores (compound)
    const flavours = modal.querySelectorAll('.compound-additional:checked')
    this.currentFoodSelections.flavours = Array.from(flavours).map(cb => ({
      text: cb.dataset.text,
      price: parseInt(cb.dataset.price, 10) || 0
    }))

    // Opcionais
    const optionals = modal.querySelectorAll('input[type="checkbox"][name^="optional_"]:checked')
    this.currentFoodSelections.optionals = Array.from(optionals).map(cb => ({
      text: cb.dataset.text
    }))
  }

  // ==========================================
  // PRICE CALCULATION
  // ==========================================

  /**
   * Calcula e atualiza preço do prato
   */
  calculateAndUpdateFoodPrice() {
    const food = this.currentFoodData
    let basePrice = 0
    let additionalsPrice = 0

    // 1. Calcular preço base
    if (food.is_pizza && food.price_calculated_by_additionals) {
      // Pizza: média dos sabores
      if (this.currentFoodSelections.flavours.length > 0) {
        const totalFlavoursPrice = this.currentFoodSelections.flavours.reduce((sum, f) => sum + f.price, 0)
        basePrice = Math.floor(totalFlavoursPrice / this.currentFoodSelections.flavours.length)
      }
    } else if (food.variable_price && this.currentFoodSelections.priceVariation) {
      // Variação de preço selecionada
      basePrice = this.currentFoodSelections.priceVariation.price
    } else {
      // Preço fixo
      basePrice = food.price
    }

    // 2. Adicionar adicionais normais
    additionalsPrice = this.currentFoodSelections.additionals.reduce((sum, a) => sum + (parseInt(a.price, 10) || 0), 0)

    // 3. Total = (base + adicionais) × quantidade
    const finalPrice = (basePrice + additionalsPrice) * this.currentFoodQuantity

    console.log('[VIPTable Menu] Price calculation:', {
      basePrice,
      additionalsPrice,
      quantity: this.currentFoodQuantity,
      finalPrice,
      additionals: this.currentFoodSelections.additionals,
      flavours: this.currentFoodSelections.flavours
    })

    // 4. Atualizar botão
    this.updateAddButtonText(finalPrice)

    return finalPrice
  }

  /**
   * Atualiza texto do botão com preço
   */
  updateAddButtonText(finalPrice) {
    const button = document.getElementById('modal-add-button')
    if (button) {
      // Sempre atualizar o texto do botão, mesmo se desabilitado
      const currentText = button.textContent
      const newText = `Adicionar ${this.formatCurrency(finalPrice)}`
      
      // Só atualizar se for diferente para evitar reflows desnecessários
      if (!currentText.includes('ESTAMOS FECHADOS') && currentText !== newText) {
        button.textContent = newText
        console.log('[VIPTable Menu] Button text updated:', newText)
      }
    }
  }

  // ==========================================
  // FORM VALIDATION
  // ==========================================

  /**
   * Valida formulário do modal
   */
  validateModalForm() {
    const food = this.currentFoodData
    const button = document.getElementById('modal-add-button')
    const warning = document.getElementById('modal-validation-warning')

    if (!button) return

    let isValid = true
    let warningMessage = ''

    console.log('[VIPTable Menu] Validating form:', {
      foodName: food.name,
      isPizza: food.is_pizza,
      variablePrice: food.variable_price,
      selections: this.currentFoodSelections
    })

    // 1. Verificar se pizza tem sabor selecionado
    if (food.is_pizza && food.price_calculated_by_additionals) {
      if (this.currentFoodSelections.flavours.length === 0) {
        isValid = false
        warningMessage = 'Selecione pelo menos um sabor'
      }
    }

    // 2. Verificar se variação de preço está selecionada
    if (food.variable_price && !food.is_pizza && !this.currentFoodSelections.priceVariation) {
      isValid = false
      warningMessage = 'Selecione uma opção'
    }

    // 3. Verificar blocos obrigatórios
    if (food.additionals) {
      food.additionals.forEach((block, index) => {
        if (block.required_block) {
          const modalEl = document.getElementById('food-detail-modal')
          const checkedInBlock = modalEl
            ? modalEl.querySelectorAll(`input.additional-checkbox[data-block-index="${index}"]:checked`)
            : document.querySelectorAll(`input.additional-checkbox[data-block-index="${index}"]:checked`)
          console.log(`[VIPTable Menu] Checking required block ${index} "${block.question}":`, {
            required: block.required_block,
            checkedCount: checkedInBlock.length,
            selector: `input.additional-checkbox[data-block-index="${index}"]:checked`,
            allCheckboxesInBlock: (modalEl || document).querySelectorAll(`input.additional-checkbox[data-block-index="${index}"]`).length
          })
          if (checkedInBlock.length === 0) {
            isValid = false
            warningMessage = `Selecione pelo menos um item em "${block.question}"`
          }
        }
      })
    }

    console.log('[VIPTable Menu] Validation result:', { 
      isValid, 
      warningMessage,
      totalAdditionalBlocks: food.additionals?.length,
      requiredBlocks: food.additionals?.filter(b => b.required_block).length
    })

    // Atualizar UI
    if (isValid) {
      button.classList.remove('cursor-not-allowed', 'theme-btn-disabled')
      button.classList.add('theme-btn-accent')
      button.disabled = false
      if (warning) {
        warning.classList.add('hidden')
        warning.textContent = ''
      }
    } else {
      button.classList.add('cursor-not-allowed', 'theme-btn-disabled')
      button.classList.remove('theme-btn-accent')
      button.disabled = true
      if (warning) {
        warning.classList.remove('hidden')
        warning.textContent = warningMessage
      }
    }

    return isValid
  }

  // ==========================================
  // ADD TO CART FROM MODAL
  // ==========================================

  /**
   * Adiciona prato do modal ao carrinho
   */
  addFoodFromModal() {
    if (!this.companyOpenValue) {
      this.showToast('Estamos fechados no momento', 'warning')
      return
    }

    if (!this.validateModalForm()) {
      return
    }

    const food = this.currentFoodData
    // Garante que todas as seleções do modal (adicionais, sabores, opcionais) estão
    // sincronizadas antes de montar o orderData, independente de eventos change
    this.updateCurrentSelections()
    const finalPrice = this.calculateAndUpdateFoodPrice()

    // Preparar dados no formato do sistema antigo
    const foodInformations = {
      id: food.id,
      name: food.name,
      flavoursCount: food.is_pizza ? this.currentFoodSelections.flavours.length : 0
    }

    const mainPrice = {
      price: food.is_pizza && food.price_calculated_by_additionals 
        ? Math.floor(this.currentFoodSelections.flavours.reduce((sum, f) => sum + f.price, 0) / this.currentFoodSelections.flavours.length)
        : (this.currentFoodSelections.priceVariation ? this.currentFoodSelections.priceVariation.price : food.price),
      text: this.currentFoodSelections.priceVariation ? this.currentFoodSelections.priceVariation.text : ''
    }

    // Final price returned by calculateAndUpdateFoodPrice is a LINE total (unit * quantity).
    // Convert it into a per-unit price to store in cart so future quantity changes update the total correctly.
    const unitPrice = Math.floor(finalPrice / Math.max(1, this.currentFoodQuantity))

    const orderData = {
      id: food.id,
      name: food.name,
      image: food.picture_url || '',
      price: unitPrice,
      quantity: this.currentFoodQuantity,
      // Dados detalhados para o pedido
      food_informations: foodInformations,
      price_data: mainPrice,
      pizza: food.is_pizza ? 'true' : 'false',
      observations: this.currentFoodSelections.observations,
      optionals: this.currentFoodSelections.optionals,
      additionals: this.currentFoodSelections.additionals,
      flavours: this.currentFoodSelections.flavours,
      final_price: unitPrice
    }

    console.log('[VIPTable Menu] Adding food from modal:', orderData)

    // Adicionar ao carrinho (formato compatível com v2)
    this.cart.push(orderData)
    this.saveCart()
    this.updateCartUI()

    // Fechar modal
    this.closeFoodDetailModal()

    // Feedback
    this.showToast(`${food.name} adicionado ao carrinho`, 'success')
    this.animateCartButton()
  }

  /**
   * Anima botão do carrinho
   */
  animateCartButton() {
    if (this.hasFloatingCartBtnTarget) {
      this.floatingCartBtnTarget.classList.add('pulse-gold')
      setTimeout(() => {
        this.floatingCartBtnTarget.classList.remove('pulse-gold')
      }, 2000)
    }
  }

  // Continuarei com os métodos de manipulação e cálculo na próxima parte...
}
